import api from '../api/client';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Card,
    Form,
    Row,
    Col,
    Select,
    DatePicker,
    InputNumber,
    Input,
    Button,
    Tag,
    Space,
    Popconfirm,
    message,
    Modal,
    Drawer,
    Divider,
    Typography,
    Dropdown,
    Badge,
    Spin,
    Tabs,
    Table,
    Timeline,
    Empty,
    Alert,
    Descriptions,
    Progress,
    ConfigProvider,
    Tooltip,
    Statistic
} from 'antd';
import {
    PlusOutlined,
    DeleteOutlined,
    SaveOutlined,
    FileTextOutlined,
    MoreOutlined,
    PercentageOutlined,
    HeartOutlined,
    WarningOutlined,
    SwapOutlined,
    EditOutlined,
    EyeOutlined,
    HistoryOutlined,
    CheckCircleOutlined,
    ToolOutlined,
    BarChartOutlined,
    ArrowUpOutlined,
    ArrowDownOutlined,
    DollarOutlined,
    PrinterOutlined,
    ReloadOutlined,
    DownOutlined,
    MedicineBoxOutlined,
    SettingOutlined,
    ExportOutlined,
    SearchOutlined,
    TagOutlined,
    InboxOutlined,
    RiseOutlined,
    FallOutlined,
    MergeOutlined,
    RollbackOutlined,
    ExclamationCircleOutlined,
    DownloadOutlined,
    SignalFilled,
    BilibiliOutlined,
    ExceptionOutlined,
    LineChartOutlined
} from '@ant-design/icons';
import { ProTable } from '@ant-design/pro-table';
import dayjs from 'dayjs';
import config from '../config';
import { Link } from 'react-router-dom';
import { groupAccountOptions } from '../utils/account';
import { useSelector } from 'react-redux';
import { apiFetch, exportReceipts, fetchUsers } from '../api';
import ConsumptionHistoryModal from './ConsumptionHistoryModal';
import StakeholderModal from './StakeholderModal';
import { formatKES } from '../utils/currency';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

const API = api.defaults.baseURL;

// ─── utils ───────────────────────────────────────────────────────────────────
const fmt = (n, d = 2) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });
const fmtPct = (n) => `${Number(n || 0).toFixed(1)}%`;
const genKey = () => `r_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;


const STATUS_COLOR = {
    alive: 'green',
    sold: 'blue',
    mortality: 'red',
    weaned: 'cyan',
    active: 'geekblue',
    retired: 'default',
    transferred: 'purple',
    disposed: 'volcano'
};

const PAY_COLOR = {
    cash: 'green',
    unpaid: 'red',
    partially_paid: 'orange',
    fully_paid: 'cyan'
};

const ACTIONS = [
    {
        key: 'depreciation',
        label: 'Depreciation',
        icon: <ArrowDownOutlined />,
        color: '#fa8c16',
        types: ['physical']
    },
    {
        key: 'appreciation',
        label: 'Appreciation',
        icon: <ArrowUpOutlined />,
        color: '#52c41a',
        types: ['physical']
    },
    {
        key: 'revaluation',
        label: 'Revaluation',
        icon: <BarChartOutlined />,
        color: '#1677ff',
        types: ['physical', 'livestock']
    },
    {
        key: 'birth',
        label: 'Siring / Birth',
        icon: <HeartOutlined />,
        color: '#eb2f96',
        types: ['livestock']
    },
    {
        key: 'parent_mortality',
        label: 'Adult Mortality',
        icon: <WarningOutlined />,
        color: '#f5222d',
        types: ['livestock']
    },
    {
        key: 'offspring_mortality',
        label: 'Offspring Mortality',
        icon: <MedicineBoxOutlined />,
        color: '#cf1322',
        types: ['livestock']
    },
    {
        key: 'sale',
        label: 'Mark Sold',
        icon: <CheckCircleOutlined />,
        color: '#1677ff',
        types: ['physical', 'livestock']
    },
    {
        key: 'disposal',
        label: 'Disposal',
        icon: <DeleteOutlined />,
        color: '#8c8c8c',
        types: ['physical', 'livestock']
    },
    {
        key: 'movement',
        label: 'Movement',
        icon: <SwapOutlined />,
        color: '#722ed1',
        types: ['physical', 'livestock']
    },
    {
        key: 'maintenance',
        label: 'Maintenance',
        icon: <ToolOutlined />,
        color: '#13c2c2',
        types: ['physical']
    },
    {
        key: 'field_update',
        label: 'Update Fields',
        icon: <EditOutlined />,
        color: '#faad14',
        types: ['physical', 'livestock']
    }
];

// =============================================================================
// FILTER BAR — reusable for both tabs
// =============================================================================
const FilterBar = ({
    filters,
    onFilterChange,
    onReset,
    stakeholders,
    assetTypes,
    farms,
    showTypeFilter = true,
    showStatusFilter = true
}) => (
    <div
        style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 10,
            alignItems: 'flex-end'
        }}
    >
        <div>
            {/* <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Search</div> */}
            <Input
                prefix={<SearchOutlined />}
                placeholder="Tag, batch, name, trans. ID…"
                value={filters.search}
                allowClear
                onChange={(e) => onFilterChange('search', e.target.value || undefined)}
                style={{ width: 200 }}
            />
        </div>
        <div>
            {/* <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Supplier</div> */}
            <Select
                value={filters.stakeholderId}
                allowClear
                placeholder="All Suppliers"
                style={{ width: 120 }}
                onChange={(v) => onFilterChange('stakeholderId', v)}
            >
                {(stakeholders || []).map((f) => (
                    <Option key={f.id} value={String(f.id)}>
                        {f.name}
                    </Option>
                ))}
            </Select>
        </div>
        {showTypeFilter && (
            <div>
                <Select
                    value={filters?.type_slug || filters.assetType}
                    allowClear
                    placeholder="All types"
                    style={{ width: 140 }}
                    onChange={(v) => onFilterChange(filters.assetType !== undefined ? 'assetType' : 'type_slug', v)}
                >
                    {assetTypes?.map((t) => (
                        <Option key={t.slug} value={t.slug}>
                            <Text>{t.name}</Text>
                        </Option>
                    ))}
                </Select>
            </div>
        )}
        <div>
            {/* <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Farm</div> */}
            <Select
                value={filters.farm}
                allowClear
                placeholder="All farms"
                style={{ width: 120 }}
                onChange={(v) => onFilterChange('farm', v)}
            >
                {(farms || []).map((f) => (
                    <Option key={f.id} value={String(f.id)}>
                        {f.name}
                    </Option>
                ))}
            </Select>
        </div>
        <div>
            {/* <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Payment</div> */}
            <Select
                value={filters.paymentStatus}
                allowClear
                placeholder="Payment Status"
                style={{ width: 130 }}
                onChange={(v) => onFilterChange('paymentStatus', v)}
            >
                {Object.keys(PAY_COLOR).map((k) => (
                    <Option key={k} value={k}>
                        {k.replace(/_/g, ' ')}
                    </Option>
                ))}
            </Select>
        </div>
        {showStatusFilter && (
            <div>
                {/* <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Status</div> */}
                <Select
                    value={filters.status}
                    allowClear
                    placeholder="Any status"
                    style={{ width: 110 }}
                    onChange={(v) => onFilterChange('status', v)}
                >
                    {Object.keys(STATUS_COLOR).map((k) => (
                        <Option key={k} value={k}>
                            {k}
                        </Option>
                    ))}
                </Select>
            </div>
        )}
        <div>
            {/* <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Date Range</div> */}
            <RangePicker
                // size="small"
                value={filters.startDate && filters.endDate ? [dayjs(filters.startDate), dayjs(filters.endDate)] : null}
                onChange={(dates) => {
                    onFilterChange('startDate', dates?.[0]?.format('YYYY-MM-DD'));
                    onFilterChange('endDate', dates?.[1]?.format('YYYY-MM-DD'));
                }}
                style={{ width: 220 }}
            />
        </div>
        {/* <Button onClick={onReset}>Reset</Button> */}
    </div>
);

// =============================================================================
// STATS DASHBOARD
// =============================================================================
const StatsDashboard = ({ farms }) => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({
        farmId: null,
        startDate: null,
        endDate: null,
        typeSlug: null
    });

    const environment = window.location.hostname === 'localhost' ? 'development' : 'production';
    const apiUrl = config[environment].apiUrl;

    const fetchStats = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (filters.farmId) params.farmId = filters.farmId;
            if (filters.startDate) params.startDate = filters.startDate.format('YYYY-MM-DD');
            if (filters.endDate) params.endDate = filters.endDate.format('YYYY-MM-DD');
            if (filters.typeSlug) params.typeSlug = filters.typeSlug;
            const { data } = await api.get(`/api/animal-assets/stats`, { params });
            setStats(data);
        } catch (err) {
            message.error(err.response?.data?.message || 'Failed to load stats');
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    const handleFilterChange = (key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
    };

    const resetFilters = () => {
        setFilters({ farmId: null, startDate: null, endDate: null, typeSlug: null });
    };

    if (!stats && loading) return <Spin tip="Loading statistics..." />;
    if (!stats) return null;

    const { assets, invoices, byType } = stats;

    // Format currency
    const fmt = (val) => `KES ${(val || 0).toLocaleString()}`;
    const fmtPct = (val) => `${(val || 0).toFixed(1)}%`;

    // Statistics card data
    const statCards = [
        {
            title: 'Total Payables',
            value: fmt(invoices?.totalPayables || 0),
            color: '#1677ff',
            sub: `${invoices?.totalInvoices || 0} invoices`
        },
        { title: 'Total Paid', value: fmt(invoices?.totalPaid || 0), color: '#52c41a', sub: `${invoices?.paidCount || 0} fully paid` },
        {
            title: 'Balance Due',
            value: fmt(invoices?.totalBalance || 0),
            color: parseFloat(invoices?.totalBalance || 0) > 0 ? '#fa8c16' : '#52c41a',
            sub: `${invoices?.unpaidCount || 0} unpaid, ${invoices?.partialCount || 0} partial`
        },
        {
            title: 'Live Assets',
            value: (assets?.liveCount || 0).toLocaleString(),
            color: '#52c41a',
            sub: `${assets?.livestockCount || 0} livestock`
        },
        { title: 'Sold', value: (assets?.soldCount || 0).toLocaleString(), color: '#1677ff', sub: 'disposed' },
        { title: 'Mortalities', value: (assets?.mortalityCount || 0).toLocaleString(), color: '#f5222d', sub: 'deaths recorded' }
    ];

    // Table columns for byType
    const columns = [
        { title: 'Type', dataIndex: 'type_name', key: 'type', render: (text, record) => <Text>{text || record.type_slug}</Text> },
        { title: 'Count', dataIndex: 'count', key: 'count', sorter: (a, b) => a.count - b.count },
        { title: 'Alive/Active', dataIndex: 'alive', key: 'alive', sorter: (a, b) => a.alive - b.alive },
        {
            title: 'Purchase Cost',
            dataIndex: 'purchaseCost',
            key: 'purchaseCost',
            render: (val) => fmt(val),
            sorter: (a, b) => a.purchaseCost - b.purchaseCost
        },
        {
            title: 'Current Value',
            dataIndex: 'currentValue',
            key: 'currentValue',
            render: (val) => fmt(val),
            sorter: (a, b) => a.currentValue - b.currentValue
        },
        {
            title: 'Gain/Loss',
            key: 'gainLoss',
            render: (_, record) => {
                const gain = (record.currentValue || 0) - (record.purchaseCost || 0);
                const isPositive = gain >= 0;
                return (
                    <Text style={{ color: isPositive ? '#52c41a' : '#f5222d' }}>
                        {isPositive ? <RiseOutlined /> : <FallOutlined />} {fmt(Math.abs(gain))}
                    </Text>
                );
            },
            sorter: (a, b) => a.currentValue - a.purchaseCost - (b.currentValue - b.purchaseCost)
        }
    ];

    return (
        <Spin spinning={loading}>
            {/* Statistics Cards */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                {statCards.map((card) => (
                    <Col xs={24} sm={12} md={8} lg={4} key={card.title}>
                        <Card>
                            <Statistic title={card.title} value={card.value} />
                            <Text>{card.sub}</Text>
                        </Card>
                    </Col>
                ))}
            </Row>

            {/* Payment Progress */}
            {invoices && (
                <Card style={{ marginBottom: 24 }} size="small">
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                        <Text type="secondary">Payment progress across all invoices</Text>
                        <Text>{fmtPct(invoices.totalPayables > 0 ? (invoices.totalPaid / invoices.totalPayables) * 100 : 0)} paid</Text>
                    </div>
                    <Progress
                        percent={invoices.totalPayables > 0 ? Math.round((invoices.totalPaid / invoices.totalPayables) * 100) : 0}
                        strokeColor="#52c41a"
                        size="small"
                    />
                </Card>
            )}

            {/* By Type Table */}
            {byType?.length > 0 && (
                <Card
                    title="Breakdown by Animal Type"
                    extra={
                        <Row gutter={[16, 16]}>
                            <Col span={6}>
                                <Select
                                    placeholder="Filter by Farm"
                                    allowClear
                                    style={{ width: '100%' }}
                                    value={filters.farmId}
                                    onChange={(val) => handleFilterChange('farmId', val)}
                                >
                                    {farms?.map((farm) => (
                                        <Option key={farm.id} value={farm.id}>
                                            {farm.name}
                                        </Option>
                                    ))}
                                </Select>
                            </Col>
                            <Col span={8}>
                                <RangePicker
                                    style={{ width: '100%' }}
                                    value={[filters.startDate, filters.endDate]}
                                    onChange={(dates) => {
                                        handleFilterChange('startDate', dates?.[0]);
                                        handleFilterChange('endDate', dates?.[1]);
                                    }}
                                    placeholder={['Start Date', 'End Date']}
                                />
                            </Col>
                            <Col span={6}>
                                <Select
                                    placeholder="Filter by Animal Type"
                                    allowClear
                                    style={{ width: '100%' }}
                                    value={filters.typeSlug}
                                    onChange={(val) => handleFilterChange('typeSlug', val)}
                                >
                                    {byType?.map((type) => (
                                        <Option key={type.type_slug} value={type.type_slug}>
                                            {type.type_name || type.type_slug}
                                        </Option>
                                    ))}
                                </Select>
                            </Col>
                            <Col span={4}>
                                <Button icon={<ReloadOutlined />} onClick={resetFilters}>
                                    Reset
                                </Button>
                            </Col>
                        </Row>
                    }
                    size="small"
                >
                    <Table size="small" columns={columns} dataSource={byType} rowKey="type_slug" pagination={false} />
                </Card>
            )}
        </Spin>
    );
};

// =============================================================================
// ACTION FORM
// =============================================================================
const ActionForm = ({ action, farms, silos, onFinish, loading, count = 1, type_slug }) => {
    const [form] = Form.useForm();
    if (!action) return null;

    // console.log('slug', type_slug);

    const submit = (v) => {
        const clean = {};
        Object.entries(v).forEach(([k, val]) => {
            if (val === null || val === undefined || val === '') return;
            clean[k] = val?.format ? val.format('YYYY-MM-DD') : val;
        });
        onFinish(clean, form);
    };

    const df = (name = 'date', label = 'Date', req = true) => (
        <Col span={12}>
            <Form.Item name={name} label={label} rules={[{ required: req }]}>
                <DatePicker style={{ width: '100%' }} />
            </Form.Item>
        </Col>
    );

    const rf = (req = false) => (
        <Col span={24}>
            <Form.Item name="remarks" label="Remarks" rules={[{ required: req }]}>
                <TextArea rows={2} />
            </Form.Item>
        </Col>
    );

    const bodies = {
        depreciation: () => (
            <Row gutter={12}>
                <Col span={12}>
                    <Form.Item name="rate" label="Rate (%)" rules={[{ required: true }]}>
                        <InputNumber min={0} max={100} step={0.5} style={{ width: '100%' }} addonAfter="%" />
                    </Form.Item>
                </Col>
                {df('effectiveDate', 'Effective Date')}
                {rf()}
            </Row>
        ),
        appreciation: () => (
            <Row gutter={12}>
                <Col span={12}>
                    <Form.Item name="rate" label="Rate (%)" rules={[{ required: true }]}>
                        <InputNumber min={0} max={100} step={0.5} style={{ width: '100%' }} addonAfter="%" />
                    </Form.Item>
                </Col>
                {df('effectiveDate', 'Effective Date')}
                {rf()}
            </Row>
        ),
        revaluation: () => (
            <Row gutter={12}>
                <Col span={12}>
                    <Form.Item name="newValue" label="New Value" rules={[{ required: true }]}>
                        <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>
                </Col>
                {df('effectiveDate', 'Effective Date')}
                {rf()}
            </Row>
        ),
        birth: () => (
            <Row gutter={12}>
                <Col span={8}>
                    <Form.Item name="siredAlive" label="Born Alive" rules={[{ required: true }]}>
                        <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item name="siredDead" label="Stillborn" initialValue={0}>
                        <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item name="siloId" label="Silo / Pen">
                        <Select allowClear>
                            {(silos || []).map((s) => (
                                <Option key={s.id} value={s.id}>
                                    {s.silo_number}
                                    {s.name ? ` — ${s.name}` : ''}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>
                {df('dateSired', 'Birth Date')}
                {rf()}
            </Row>
        ),
        parent_mortality: () => (
            <Row gutter={12}>
                {df()}
                {rf(true)}
            </Row>
        ),
        offspring_mortality: () => (
            <Row gutter={12}>
                <Col span={8}>
                    <Form.Item name="eventCount" label="Count" initialValue={1}>
                        <InputNumber min={1} style={{ width: '100%' }} />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item name="ageAtDeath" label="Age at Death">
                        <Input placeholder="e.g. 3 days" />
                    </Form.Item>
                </Col>
                {df()}
                {rf(true)}
            </Row>
        ),
        sale: () => (
            <Row gutter={12}>
                <Col span={8}>
                    <Form.Item name="eventCount" label="Count" initialValue={1}>
                        <InputNumber min={1} style={{ width: '100%' }} />
                    </Form.Item>
                </Col>
                {df()}
                {rf()}
            </Row>
        ),
        disposal: () => (
            <Row gutter={12}>
                {df()}
                {rf()}
            </Row>
        ),
        movement: () => (
            <Row gutter={12}>
                <Col span={10}>
                    <Form.Item name="farmTo" label="To Farm" rules={[{ required: true }]}>
                        <Select>
                            {(farms || []).map((f) => (
                                <Option key={f.id} value={String(f.id)}>
                                    {f.name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>
                <Col span={7}>
                    <Form.Item name="siloId" label="To Silo">
                        <Select allowClear>
                            {(silos || []).map((s) => (
                                <Option key={s.id} value={s.id}>
                                    {s.silo_number}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>
                {df('recorded', 'Date')}
                {rf()}
            </Row>
        ),
        maintenance: () => (
            <Row gutter={12}>
                <Col span={10}>
                    <Form.Item name="cost" label="Cost">
                        <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>
                </Col>
                {df()}
                {rf(true)}
            </Row>
        ),
        field_update: () => (
            <Row gutter={12}>
                <Col span={8}>
                    <Form.Item name="type_slug" label="Type">
                        <Select allowClear>
                            {type_slug?.map((s) => (
                                <Option key={s.id} value={s.slug}>
                                    {s.name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item name="status" label="Status">
                        <Select allowClear>
                            {Object.keys(STATUS_COLOR).map((s) => (
                                <Option key={s} value={s}>
                                    {s}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item name="health" label="Health">
                        <Select allowClear>
                            {['healthy', 'sick', 'injured', 'quarantined'].map((h) => (
                                <Option key={h} value={h}>
                                    {h}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item name="weight" label="Weight (kg)">
                        <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item name="farm" label="Farm">
                        <Select allowClear>
                            {(farms || []).map((f) => (
                                <Option key={f.id} value={String(f.id)}>
                                    {f.name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item name="paymentStatus" label="Payment">
                        <Select allowClear>
                            {Object.keys(PAY_COLOR).map((k) => (
                                <Option key={k} value={k}>
                                    {k.replace(/_/g, ' ')}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item name="gender" label="Gender">
                        <Select allowClear placeholder="Auto-detect">
                            <Option value="male">Male</Option>
                            <Option value="female">Female</Option>
                            <Option value="unknown">Unknown</Option>
                        </Select>
                    </Form.Item>
                </Col>

                {rf()}
            </Row>
        )
    };

    return (
        <Form form={form} layout="vertical" onFinish={submit}>
            {bodies[action]?.()}
            <Button type="primary" htmlType="submit" loading={loading} block size="large" style={{ marginTop: 4 }}>
                Apply{count > 1 ? ` to ${count} records` : ''}
            </Button>
        </Form>
    );
};

const handleDownloadReceipt = async (record) => {
    await exportReceipts({ id: record.id, format: 'pdf' });
};

// =============================================================================
// PAYMENT PANEL
// =============================================================================

const PaymentPanel = ({ invoice, payments, onSaved, currentUser, users, paidOptions }) => {
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    if (!invoice) return null;

    const total = parseFloat(invoice.totalCost || 0);
    const paid = parseFloat(invoice.amountPaidTotal || 0);
    const outstanding = Math.max(0, total - paid);
    const pct = total > 0 ? Math.min(100, Math.round((paid / total) * 100)) : 0;

    const onSubmit = async (v) => {
        setSaving(true);
        try {
            await apiFetch(`/api/animal-assets/invoices/${invoice.id}/payment`, {
                method: 'POST',
                body: JSON.stringify({
                    amount: v.amount,
                    paymentDate: v.paymentDate?.format('YYYY-MM-DD'),
                    paymentMethod: v.paymentMethod,
                    reference: v.reference,
                    notes: v.notes,
                    userId: currentUser?.id,
                    userName: currentUser?.name,
                    creditAccount: v.creditAccount,
                    pay_reference: v.pay_reference,
                    gl_debit: invoice.gl_debit,
                    gl_credit: invoice.gl_credit,
                    tableName: 'asset_invoice_headers',
                    record_id: invoice.id,
                    stakeholderId: invoice.stakeholderId,
                    paidBy: v.paidBy || currentUser?.id
                })
            });
            message.success('Payment recorded.');
            form.resetFields();
            onSaved?.();
        } catch (e) {
            message.error(e.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div>
            <Row gutter={12} style={{ marginBottom: 16 }}>
                {[
                    { label: 'Invoice Total', value: fmt(total), bg: '#f0f5ff', color: '#1677ff' },
                    { label: 'Total Paid', value: fmt(paid), bg: '#f6ffed', color: '#52c41a' },
                    {
                        label: 'Outstanding',
                        value: fmt(outstanding),
                        bg: outstanding > 0 ? '#fff7e6' : '#f6ffed',
                        color: outstanding > 0 ? '#fa8c16' : '#52c41a'
                    }
                ].map((s) => (
                    <Col span={8} key={s.label}>
                        <div
                            style={{
                                textAlign: 'center',
                                background: s.bg,
                                borderRadius: 8,
                                padding: '10px 6px',
                                border: '1px solid #f0f0f0'
                            }}
                        >
                            <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>{s.label}</div>
                            <div style={{ fontSize: 17, fontWeight: 600, color: s.color }}>{s.value}</div>
                        </div>
                    </Col>
                ))}
            </Row>
            <Progress
                percent={pct}
                strokeColor={pct === 100 ? '#52c41a' : '#1677ff'}
                size="small"
                format={(p) => `${p}% paid`}
                style={{ marginBottom: 16 }}
            />

            {invoice.paymentStatus !== 'fully_paid' && (
                // && invoice.paymentStatus !== 'cash'
                <>
                    <Divider orientation="left" style={{ fontSize: 13 }}>
                        Record Payment
                    </Divider>
                    <Form form={form} layout="vertical" onFinish={onSubmit}>
                        <Row gutter={12}>
                            <Col span={10}>
                                <Form.Item
                                    name="amount"
                                    label="Amount"
                                    rules={[
                                        { required: true },
                                        {
                                            validator: (_, v) =>
                                                v > outstanding ? Promise.reject(`Max: ${fmt(outstanding)}`) : Promise.resolve()
                                        }
                                    ]}
                                >
                                    <InputNumber
                                        min={0.01}
                                        max={outstanding}
                                        style={{ width: '100%' }}
                                        formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={14}>
                                <Form.Item name="paymentDate" label="Payment Date" rules={[{ required: true }]} initialValue={dayjs()}>
                                    <DatePicker style={{ width: '100%' }} />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="paymentMethod" label="Method" rules={[{ required: true }]}>
                                    <Select allowClear>
                                        {['Cash', 'Bank Transfer', 'Cheque', 'Mobile Money'].map((m) => (
                                            <Option key={m} value={m.toLowerCase().replace(/ /g, '_')}>
                                                {m}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="paidBy" label="Paid By" rules={[{ required: true }]}>
                                    <Select>
                                        {users.map((u) => (
                                            <Option key={u.id} value={u.id}>
                                                {u.firstname} {u.lastname}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="pay_reference" label="Reference / Cheque #">
                                    <Input placeholder="Bank ref, cheque #…" />
                                </Form.Item>
                            </Col>
                            <Form.Item
                                name="creditAccount"
                                label="Credit Account"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Please select credit account'
                                    }
                                ]}
                            >
                                <Select options={paidOptions} placeholder="Select credit account" showSearch optionFilterProp="label" />
                            </Form.Item>
                            <Col span={24}>
                                <Form.Item name="notes" label="Notes">
                                    <TextArea rows={2} />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Button type="primary" htmlType="submit" icon={<DollarOutlined />} loading={saving} block>
                            Save Payment
                        </Button>
                    </Form>
                </>
            )}

            {payments?.length > 0 && (
                <>
                    <Divider orientation="left" style={{ fontSize: 13, marginTop: 20 }}>
                        Payment History
                    </Divider>
                    <Table
                        size="small"
                        rowKey="id"
                        pagination={false}
                        dataSource={payments}
                        columns={[
                            { title: 'Date', dataIndex: 'paymentDate', width: 100, render: (v) => dayjs(v).format('DD MMM YY') },
                            {
                                title: 'Amount',
                                dataIndex: 'amount',
                                width: 110,
                                align: 'right',
                                render: (v) => (
                                    <Text strong style={{ color: '#52c41a' }}>
                                        {fmt(v)}
                                    </Text>
                                )
                            },
                            { title: 'Method', dataIndex: 'paymentMethod', width: 110, render: (v) => v?.replace(/_/g, ' ') || '—' },
                            { title: 'Ref', dataIndex: 'pay_reference', render: (v) => v || '—' },
                            { title: 'By', dataIndex: 'paidBy', width: 90 },
                            {
                                title: 'Action',
                                key: 'action',
                                render: (_, record) => (
                                    <Button icon={<DownloadOutlined />} size="small" onClick={() => handleDownloadReceipt(record)}>
                                        Receipt
                                    </Button>
                                )
                            }
                        ]}
                    />
                </>
            )}
        </div>
    );
};

// =============================================================================
// INVOICE DETAIL DRAWER
// =============================================================================
const InvoiceDrawer = ({ invoiceId, defaultTab = 'items', open, onClose, currentUser, users, paidOptions, onRefresh }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [tab, setTab] = useState(defaultTab);

    const load = useCallback(() => {
        if (!invoiceId || !open) return;
        setLoading(true);
        apiFetch(`/api/animal-assets/invoices/${invoiceId}`)
            .then(setData)
            .catch((e) => message.error(e.message))
            .finally(() => setLoading(false));
    }, [invoiceId, open]);

    useEffect(() => {
        load();
        setTab(defaultTab);
    }, [load, defaultTab]);

    const itemCols = [
        { title: 'Type', width: 100, render: (_, r) => <Tag color={r.typeColor}>{r.typeName}</Tag> },
        { title: 'Subtype', dataIndex: 'subtypeName', width: 90, render: (v) => v || '—' },
        {
            title: 'Tag/Batch',
            width: 130,
            render: (_, r) => (
                <Text
                    copyable={{ text: r.tagNumber || r.batchId || '' }}
                    style={{ fontFamily: 'monospace', color: '#1677ff', fontSize: 12 }}
                >
                    {r.tagNumber || r.batchId || '—'}
                </Text>
            )
        },
        { title: 'Qty', dataIndex: 'quantity', width: 55, align: 'right' },
        { title: 'Cost', dataIndex: 'cost', width: 90, align: 'right', render: (v) => fmt(v) },
        { title: 'Total', dataIndex: 'totalCost', width: 110, align: 'right', render: (v) => <Text strong>{fmt(v)}</Text> },
        { title: 'Farm', dataIndex: 'farm', width: 80 },
        { title: 'Silo', dataIndex: 'siloNumDisplay', width: 70, render: (v) => v || '—' },
        { title: 'Status', dataIndex: 'status', width: 90, render: (v) => <Tag color={STATUS_COLOR[v]}>{v}</Tag> }
    ];

    return (
        <Drawer
            title={
                data && (
                    <Space>
                        <FileTextOutlined />
                        <Text strong style={{ fontSize: 13 }}>
                            {data.header?.transactionId}
                        </Text>
                        <Tag color={PAY_COLOR[data.header?.paymentStatus]}>{data.header?.paymentStatus?.replace(/_/g, ' ')}</Tag>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {data.header?.recorded ? dayjs(data.header.recorded).format('DD MMM YYYY') : ''}
                        </Text>
                    </Space>
                )
            }
            open={open}
            onClose={onClose}
            width={860}
            extra={
                <Button icon={<PrinterOutlined />} size="small" onClick={() => window.print()}>
                    Print
                </Button>
            }
        >
            <Spin spinning={loading}>
                {data && (
                    <Tabs
                        activeKey={tab}
                        onChange={setTab}
                        items={[
                            {
                                key: 'items',
                                label: (
                                    <Space>
                                        <FileTextOutlined />
                                        Items ({data.items?.length || 0})
                                    </Space>
                                ),
                                children: (
                                    <>
                                        <Descriptions size="small" bordered column={3} style={{ marginBottom: 12 }}>
                                            <Descriptions.Item label="Date">
                                                {data.header.recorded ? dayjs(data.header.recorded).format('DD MMM YYYY') : '—'}
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Farm">{data.header.farm || '—'}</Descriptions.Item>
                                            <Descriptions.Item label="Type">{data.header.assetType || '—'}</Descriptions.Item>
                                            <Descriptions.Item label="GL Debit">{data.header.gl_debit || '—'}</Descriptions.Item>
                                            <Descriptions.Item label="GL Credit">{data.header.gl_credit || '—'}</Descriptions.Item>
                                            <Descriptions.Item label="Remarks">{data.header.remarks || '—'}</Descriptions.Item>
                                        </Descriptions>
                                        <Table
                                            size="small"
                                            rowKey="id"
                                            columns={itemCols}
                                            dataSource={data.items || []}
                                            pagination={false}
                                            scroll={{ x: 750 }}
                                            summary={(rows) => (
                                                <Table.Summary.Row>
                                                    <Table.Summary.Cell index={0} colSpan={4}>
                                                        <Text strong>Total</Text>
                                                    </Table.Summary.Cell>
                                                    <Table.Summary.Cell index={4} align="right" />
                                                    <Table.Summary.Cell index={5} align="right">
                                                        <Text strong style={{ color: '#1677ff' }}>
                                                            {fmt(rows.reduce((s, r) => s + parseFloat(r.totalCost || 0), 0))}
                                                        </Text>
                                                    </Table.Summary.Cell>
                                                    <Table.Summary.Cell index={6} colSpan={3} />
                                                </Table.Summary.Row>
                                            )}
                                        />
                                    </>
                                )
                            },
                            {
                                key: 'payments',
                                label: (
                                    <Space>
                                        <DollarOutlined />
                                        Payments
                                        {data.header?.paymentStatus === 'fully_paid' ? (
                                            <Tag color="green" style={{ marginLeft: 4 }}>
                                                Paid
                                            </Tag>
                                        ) : parseFloat(data.header?.outstanding || 0) > 0 ? (
                                            <Tag color="red" style={{ marginLeft: 4 }}>
                                                {fmt(data.header.outstanding, 0)} due
                                            </Tag>
                                        ) : null}
                                    </Space>
                                ),
                                children: (
                                    <PaymentPanel
                                        invoice={data.header}
                                        payments={data.payments}
                                        currentUser={currentUser}
                                        users={users}
                                        paidOptions={paidOptions}
                                        onSaved={() => {
                                            load();
                                            onRefresh?.();
                                        }}
                                    />
                                )
                            }
                        ]}
                    />
                )}
            </Spin>
        </Drawer>
    );
};

const StatementDrawer = ({ assetId, open, onClose }) => {
    const [data, setData] = useState(null);
    const [loading, setL] = useState(false);
    const [tab, setTab] = useState('overview');
    const [consumptionModalOpen, setConsumptionModalOpen] = useState(false);
    const [weightSales, setWeightSales] = useState([]);
    const [loadingWeightSales, setLoadingWeightSales] = useState(false);

    useEffect(() => {
        if (!open || !assetId) return;
        setL(true);
        Promise.all([apiFetch(`/api/animal-assets/${assetId}/statement`), fetchWeightSalesHistory(assetId)])
            .then(([statementData, weightSalesData]) => {
                setData(statementData);
                setWeightSales(weightSalesData);
            })
            .catch((e) => message.error(e.message))
            .finally(() => setL(false));
    }, [open, assetId]);

    const fetchWeightSalesHistory = async (assetId) => {
        try {
            const response = await api.get(`/animal-assets/${assetId}/weight-sales`);
            return response.data;
        } catch (error) {
            console.error('Error fetching weight sales:', error);
            return [];
        }
    };

    const eventIcon = {
        purchase: <FileTextOutlined style={{ color: '#1677ff' }} />,
        birth: <HeartOutlined style={{ color: '#eb2f96' }} />,
        mortality: <WarningOutlined style={{ color: '#f5222d' }} />,
        sale: <CheckCircleOutlined style={{ color: '#1677ff' }} />,
        weight_sale: <LineChartOutlined style={{ color: '#fa8c16' }} />,
        movement: <SwapOutlined style={{ color: '#722ed1' }} />,
        depreciation: <ArrowDownOutlined style={{ color: '#fa8c16' }} />,
        appreciation: <ArrowUpOutlined style={{ color: '#52c41a' }} />,
        revaluation: <BarChartOutlined style={{ color: '#1677ff' }} />,
        field_update: <EditOutlined style={{ color: '#13c2c2' }} />,
        maintenance: <ToolOutlined style={{ color: '#13c2c2' }} />,
        disposal: <DeleteOutlined style={{ color: '#8c8c8c' }} />,
        egg_collection: <InboxOutlined style={{ color: '#fadb14' }} />,
        offspring_tagged: <TagOutlined style={{ color: '#722ed1' }} />,
        consumption: <InboxOutlined style={{ color: '#fa541c' }} />,
        weaned: <MergeOutlined style={{ color: '#52c41a' }} />,
        offspring_weaned: <MergeOutlined style={{ color: '#fa8c16' }} />
    };

    if (!data && loading)
        return (
            <Drawer open={open} onClose={onClose} width={880}>
                <Spin />
            </Drawer>
        );
    if (!data)
        return (
            <Drawer open={open} onClose={onClose} width={880}>
                <Empty description="No data" />
            </Drawer>
        );

    const { asset, ledger, summary } = data;
    const identifier = asset.tagNumber || asset.batchId;
    const displayName = asset.animalName || identifier || asset.assetName;

    // Filter ledger by type
    const consumptions = ledger.filter((e) => e.type === 'consumption');
    const sales = ledger.filter((e) => e.type === 'sale');
    const eggs = ledger.filter((e) => e.type === 'egg_collection');
    const weightSalesEvents = ledger.filter((e) => e.type === 'weight_sale');

    // Calculate weight statistics
    const totalWeightSold = weightSales.reduce((sum, w) => sum + (w.weight_sold || 0), 0);
    const totalWeightRevenue = weightSales.reduce((sum, w) => sum + (w.total_amount || 0), 0);
    const remainingWeight = (asset.current_weight || asset.weight || 0) - totalWeightSold;

    // Calculate total revenue including weight sales
    const totalRevenue = (summary.totalRevenue || 0) + totalWeightRevenue;

    // Columns for weight sales table
    const weightSalesCols = [
        { title: 'Date', dataIndex: 'sale_date', width: 100, render: (v) => dayjs(v).format('DD MMM YYYY') },
        { title: 'Weight Sold (kg)', dataIndex: 'weight_sold', width: 100, align: 'right', render: (v) => `${v} kg` },
        { title: 'Price/kg', dataIndex: 'price_per_kg', width: 100, align: 'right', render: (v) => formatKES(v) },
        { title: 'Total', dataIndex: 'total_amount', width: 110, align: 'right', render: (v) => formatKES(v) },
        { title: 'Remaining (kg)', dataIndex: 'remaining_weight', width: 100, align: 'right', render: (v) => `${v} kg` },
        { title: 'Customer', dataIndex: 'customer_name', width: 120, ellipsis: true },
        { title: 'Sale #', dataIndex: 'sale_transaction', width: 120 }
    ];

    const consumptionLedger = ledger.filter((e) => e.type === 'consumption');

    //     // Columns for sales table
    const salesCols = [
        { title: 'Date', dataIndex: 'date', width: 100, render: (v) => (v ? dayjs(v).format('DD MMM YY') : '—') },
        { title: 'Description', dataIndex: 'description' },
        { title: 'Qty', dataIndex: 'quantity', width: 60, align: 'right' },
        { title: 'Unit Price', dataIndex: 'unitPrice', width: 90, align: 'right', render: (v) => fmt(v) },
        {
            title: 'Total',
            dataIndex: 'amount',
            width: 100,
            align: 'right',
            render: (v) => (
                <Text strong style={{ color: '#52c41a' }}>
                    {fmt(v)}
                </Text>
            )
        }
    ];

    // Columns for eggs table
    const eggsCols = [
        { title: 'Date', dataIndex: 'date', width: 100, render: (v) => (v ? dayjs(v).format('DD MMM YYYY') : '—') },
        { title: 'Trays', dataIndex: 'traysCount', width: 70, align: 'right' },
        { title: 'Eggs', dataIndex: 'eggsCount', width: 70, align: 'right' },
        { title: 'Remarks', dataIndex: 'remarks', ellipsis: true }
    ];

    // Get current weight display
    const currentWeight = asset.current_weight || asset.weight || 0;
    const weightSoldTotal = asset.weight_sold_total || totalWeightSold;

    // ✅ CONSUMPTIONS COLUMNS - Updated for per-animal display
    const consCols = [
        {
            title: 'Date',
            dataIndex: 'date',
            width: 100,
            render: (v) => (v ? dayjs(v).format('DD MMM YY') : '—')
        },
        {
            title: 'Item',
            dataIndex: 'itemName',
            render: (v) => v || '—'
        },
        {
            title: 'Qty (This Animal)',
            dataIndex: 'perAnimalQuantity',
            width: 90,
            align: 'right',
            render: (v) => (v ? `${v.toFixed(2)} ${data?.consumptions?.[0]?.itemUnit || 'kg'}` : '—')
        },
        {
            title: 'Total Qty',
            dataIndex: 'quantity',
            width: 80,
            align: 'right',
            render: (v) => (v ? `${v.toFixed(2)}` : '—')
        },
        {
            title: 'Animals',
            dataIndex: 'animalCount',
            width: 60,
            align: 'center',
            render: (v) => v || 1
        },
        {
            title: 'Unit Cost',
            dataIndex: 'unitCost',
            width: 85,
            align: 'right',
            render: (v) => formatKES(v || 0)
        },
        {
            title: 'Cost/Animal',
            dataIndex: 'perAnimalCost',
            width: 90,
            align: 'right',
            render: (v) => formatKES(v || 0)
        },
        {
            title: 'Purpose',
            dataIndex: 'purpose',
            width: 100,
            render: (v) => v || '-'
        }
    ];

    // Calculate per-animal summary
    const totalConsumptionQty = summary.totalConsumptionForThisAnimal || 0;
    const totalConsumptionCost = summary.totalConsumptionCostForThisAnimal || 0;
    const totalFeedings = summary.totalFeedings || 0;
    const avgPerFeeding = summary.avgPerFeeding || 0;

    return (
        <Drawer
            title={
                <Space>
                    <BarChartOutlined />
                    <span style={{ fontWeight: 600 }}>{displayName}</span>
                    <Tag color={asset.typeColor || 'blue'}>{asset.typeName}</Tag>
                    <Tag color={STATUS_COLOR[asset.status]}>{asset.status}</Tag>
                </Space>
            }
            open={open}
            onClose={onClose}
            width={880}
            extra={
                <Space>
                    <Button
                        icon={<ExportOutlined />}
                        size="small"
                        onClick={() => doExport(`/api/animal-assets/${assetId}/statement/export`)}
                    >
                        Export CSV
                    </Button>
                    <Button icon={<InboxOutlined />} size="small" onClick={() => setConsumptionModalOpen(true)}>
                        Consumption History
                    </Button>
                </Space>
            }
        >
            <Spin spinning={loading}>
                {/* Per-Animal Consumption Summary Cards */}
                <Row gutter={10} style={{ marginBottom: 14 }}>
                    {[
                        { label: 'Total Consumed', value: `${totalConsumptionQty.toFixed(2)} kg` },
                        { label: 'Total Consumption Cost', value: formatKES(totalConsumptionCost) },
                        { label: 'Purchase Cost', value: fmt(summary.purchaseCost) },
                        { label: 'Consumption Cost', value: fmt(summary.totalConsumptionCost) },
                        { label: 'Total Investment', value: fmt(summary.totalInvestment) },
                        { label: 'Regular Sales', value: fmt(summary.totalRevenue) },
                        { label: 'Weight Sales', value: fmt(totalWeightRevenue) },
                        { label: 'Total Revenue', value: fmt(totalRevenue) },
                        {
                            label: totalRevenue - summary.totalInvestment >= 0 ? 'Profit' : 'Loss',
                            value: fmt(Math.abs(totalRevenue - summary.totalInvestment)),
                            color: totalRevenue - summary.totalInvestment >= 0 ? '#52c41a' : '#f5222d'
                        },
                        {
                            label: 'Return',
                            value: `${
                                summary.totalInvestment > 0
                                    ? (((totalRevenue - summary.totalInvestment) / summary.totalInvestment) * 100).toFixed(1)
                                    : 0
                            }%`,
                            color: totalRevenue - summary.totalInvestment >= 0 ? '#52c41a' : '#f5222d'
                        },
                        { label: 'No of Consumption', value: totalFeedings, color: '#722ed1' },
                        { label: 'Avg per Feeding', value: `${avgPerFeeding.toFixed(2)} kg`, color: '#fa8c16' }
                    ].map((s) => (
                        <Col span={6} key={s.label}>
                            <div
                                style={{
                                    textAlign: 'center',
                                    background: '#fafafa',
                                    borderRadius: 8,
                                    padding: '8px 4px',
                                    border: '1px solid #f0f0f0'
                                }}
                            >
                                <div style={{ fontSize: 10, color: '#888', marginBottom: 1 }}>{s.label}</div>
                                <div style={{ fontSize: 15, fontWeight: 700, color: s.color }}>{s.value}</div>
                            </div>
                        </Col>
                    ))}
                </Row>

                {/* P&L summary cards */}
                <Row gutter={10} style={{ marginBottom: 14 }}>
                    {[
                        { label: 'Purchase Cost', value: formatKES(summary.purchaseCost) },
                        { label: 'Consumption Cost', value: formatKES(summary.totalConsumptionCost) },
                        { label: 'Total Investment', value: formatKES(summary.totalInvestment) },
                        { label: 'Total Revenue', value: formatKES(summary.totalRevenue)},
                        {
                            label: summary.isProfit ? 'Profit' : 'Loss',
                            value: formatKES(Math.abs(summary.profitLoss)),
                            color: summary.isProfit ? '#52c41a' : '#f5222d'
                        },
                        { label: 'Return', value: `${summary.profitLossPercent}%`, color: summary.isProfit ? '#52c41a' : '#f5222d' }
                    ].map((s) => (
                        <Col span={4} key={s.label}>
                            <div
                                style={{
                                    textAlign: 'center',
                                    background: '#fafafa',
                                    borderRadius: 8,
                                    padding: '8px 4px',
                                    border: '1px solid #f0f0f0'
                                }}
                            >
                                <div style={{ fontSize: 10, color: '#888', marginBottom: 1 }}>{s.label}</div>
                                <div style={{ fontSize: 15, fontWeight: 700, color: s.color }}>{s.value}</div>
                            </div>
                        </Col>
                    ))}
                </Row>

                {(asset.type_slug === 'goat' || asset.type_slug === 'sheep' || asset.type_slug === 'pig') && (
                    <Row gutter={10} style={{ marginBottom: 14 }}>
                        <Col span={6}>
                            <div
                                style={{
                                    textAlign: 'center',
                                    background: '#e6f7ff',
                                    borderRadius: 8,
                                    padding: '8px 4px',
                                    border: '1px solid #91d5ff'
                                }}
                            >
                                <div style={{ fontSize: 10, color: '#888', marginBottom: 1 }}>Initial Weight</div>
                                <div style={{ fontSize: 16, fontWeight: 700, color: '#1890ff' }}>
                                    {asset.initial_weight || currentWeight} kg
                                </div>
                            </div>
                        </Col>
                        <Col span={6}>
                            <div
                                style={{
                                    textAlign: 'center',
                                    background: '#fff7e6',
                                    borderRadius: 8,
                                    padding: '8px 4px',
                                    border: '1px solid #ffd591'
                                }}
                            >
                                <div style={{ fontSize: 10, color: '#888', marginBottom: 1 }}>Weight Sold</div>
                                <div style={{ fontSize: 16, fontWeight: 700, color: '#fa8c16' }}>{weightSoldTotal} kg</div>
                            </div>
                        </Col>
                        <Col span={6}>
                            <div
                                style={{
                                    textAlign: 'center',
                                    background: '#f6ffed',
                                    borderRadius: 8,
                                    padding: '8px 4px',
                                    border: '1px solid #b7eb8f'
                                }}
                            >
                                <div style={{ fontSize: 10, color: '#888', marginBottom: 1 }}>Current Weight</div>
                                <div style={{ fontSize: 16, fontWeight: 700, color: '#52c41a' }}>{currentWeight} kg</div>
                            </div>
                        </Col>
                        <Col span={6}>
                            <div
                                style={{
                                    textAlign: 'center',
                                    background: '#f9f0ff',
                                    borderRadius: 8,
                                    padding: '8px 4px',
                                    border: '1px solid #d3adf7'
                                }}
                            >
                                <div style={{ fontSize: 10, color: '#888', marginBottom: 1 }}>Avg Price/kg</div>
                                <div style={{ fontSize: 16, fontWeight: 700, color: '#722ed1' }}>
                                    {weightSales.length > 0 ? formatKES(totalWeightRevenue / totalWeightSold) : formatKES(0)}
                                </div>
                            </div>
                        </Col>
                    </Row>
                )}

                {/* Livestock stats */}
                {asset.category === 'livestock' && (
                    <Row gutter={8} style={{ marginBottom: 14 }}>
                        {[
                            { label: 'Born Alive', value: summary.totalBorn || 0 },
                            { label: 'Stillborn', value: summary.totalStillborn || 0 },
                            { label: 'Offspring Deaths', value: summary.offspringMortalities || 0 },
                            { label: 'Adult Deaths', value: summary.parentMortalities || 0 },
                            { label: 'Movements', value: summary.movements || 0 },
                            ...(asset.is_batch
                                ? [
                                      { label: 'Total Eggs', value: summary.totalEggs || 0 },
                                      { label: 'Trays', value: (summary.totalEggTrays || 0).toFixed(1) }
                                  ]
                                : [])
                        ].map((s) => (
                            <Col key={s.label} style={{ textAlign: 'center', minWidth: 70 }}>
                                <div style={{ fontSize: 18, fontWeight: 700, color: s.color }}>{s.value}</div>
                                <div style={{ fontSize: 10, color: '#888' }}>{s.label}</div>
                            </Col>
                        ))}
                    </Row>
                )}

                {/* Asset details */}
                <Descriptions size="small" bordered column={2} style={{ marginBottom: 12 }}>
                    <Descriptions.Item label="Type">{asset.typeName}</Descriptions.Item>
                    <Descriptions.Item label="Subtype">{asset.subtypeName || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Tag / Batch">
                        <Text copyable style={{ fontFamily: 'monospace' }}>
                            {identifier || '—'}
                        </Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Name">{asset.animalName || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Farm">{asset.farm || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Silo">{asset.siloNum || '—'}</Descriptions.Item>
                    {asset.category === 'livestock' && <Descriptions.Item label="Gender">{asset.gender || '—'}</Descriptions.Item>}
                    <Descriptions.Item label="Birth">{asset.birth ? dayjs(asset.birth).format('DD MMM YYYY') : '—'}</Descriptions.Item>
                    {asset.category === 'physical' && (
                        <>
                            <Descriptions.Item label="Serial #">{asset.serialNumber || '—'}</Descriptions.Item>
                            <Descriptions.Item label="Condition">{asset.conditionRating || '—'}</Descriptions.Item>
                        </>
                    )}
                </Descriptions>

                <Tabs
                    activeKey={tab}
                    onChange={setTab}
                    size="small"
                    items={[
                        {
                            key: 'overview',
                            label: 'Timeline',
                            children:
                                ledger.length === 0 ? (
                                    <Empty description="No transactions" />
                                ) : (
                                    <Timeline
                                        items={ledger.map((entry, idx) => ({
                                            dot: eventIcon[entry.eventType] || eventIcon[entry.type] || <HistoryOutlined />,
                                            children: (
                                                <div key={idx} style={{ marginBottom: 4 }}>
                                                    <Space wrap>
                                                        <Text strong style={{ textTransform: 'capitalize', fontSize: 13 }}>
                                                            {(entry.eventType || entry.type).replace(/_/g, ' ')}
                                                        </Text>
                                                        <Text type="secondary" style={{ fontSize: 11 }}>
                                                            {dayjs(entry.date).format('DD MMM YYYY')}
                                                        </Text>
                                                        {entry.runningValue !== undefined && (
                                                            <Text
                                                                style={{ fontSize: 11, color: entry.amount >= 0 ? '#52c41a' : '#f5222d' }}
                                                            >
                                                                → {formatKES(entry.runningValue)}
                                                            </Text>
                                                        )}
                                                    </Space>
                                                    {entry.description && <div style={{ fontSize: 12 }}>{entry.description}</div>}
                                                    {entry.perAnimalQuantity && (
                                                        <div style={{ fontSize: 12, color: '#666' }}>
                                                            Per animal: {entry.perAnimalQuantity.toFixed(2)} kg
                                                        </div>
                                                    )}
                                                    {entry.remarks && <div style={{ fontSize: 12, color: '#888' }}>{entry.remarks}</div>}
                                                    {entry.userName && (
                                                        <div style={{ fontSize: 11, color: '#bbb' }}>by {entry.userName}</div>
                                                    )}
                                                </div>
                                            )
                                        }))}
                                    />
                                )
                        },
                        {
                            key: 'consumptions',
                            label: `Consumptions (${consumptions?.length || 0})`,
                            children:
                                !consumptions || consumptions.length === 0 ? (
                                    <Empty description="No consumption records" />
                                ) : (
                                    <>
                                        <Table
                                            size="small"
                                            rowKey="id"
                                            columns={consCols}
                                            dataSource={consumptions}
                                            pagination={{ pageSize: 10 }}
                                            summary={(rows) => (
                                                <Table.Summary.Row>
                                                    <Table.Summary.Cell colSpan={2}>
                                                        <Text strong>Totals for this Animal</Text>
                                                    </Table.Summary.Cell>
                                                    <Table.Summary.Cell align="right">
                                                        <Text strong>
                                                            {rows.reduce((s, r) => s + (r.perAnimalQuantity || 0), 0).toFixed(2)} kg
                                                        </Text>
                                                    </Table.Summary.Cell>
                                                    <Table.Summary.Cell />
                                                    <Table.Summary.Cell />
                                                    <Table.Summary.Cell />
                                                    <Table.Summary.Cell align="right">
                                                        <Text strong style={{ color: '#fa8c16' }}>
                                                            {formatKES(rows.reduce((s, r) => s + (r.perAnimalCost || 0), 0))}
                                                        </Text>
                                                    </Table.Summary.Cell>
                                                    <Table.Summary.Cell />
                                                </Table.Summary.Row>
                                            )}
                                        />
                                        <div style={{ marginTop: 12, padding: 12, background: '#f6ffed', borderRadius: 8 }}>
                                            <Text strong>Consumption Summary for this Animal:</Text>
                                            <div style={{ marginTop: 8 }}>
                                                <Row gutter={16}>
                                                    <Col span={8}>
                                                        <Statistic
                                                            title="Total Consumed"
                                                            value={totalConsumptionQty.toFixed(2)}
                                                            suffix="kg"
                                                            size="small"
                                                        />
                                                    </Col>
                                                    <Col span={8}>
                                                        <Statistic
                                                            title="Total Cost"
                                                            value={formatKES(totalConsumptionCost)}
                                                            size="small"
                                                        />
                                                    </Col>
                                                    <Col span={8}>
                                                        <Statistic
                                                            title="Total Consumption"
                                                            value={totalFeedings.toFixed(2)}
                                                            size="small"
                                                        />
                                                    </Col>
                                                </Row>
                                            </div>
                                        </div>
                                    </>
                                )
                        },
                        {
                            key: 'weight_sales',
                            label: `Weight Sales (${weightSales.length})`,
                            children:
                                weightSales.length === 0 ? (
                                    <Empty description="No weight sales recorded" />
                                ) : (
                                    <>
                                        <Table
                                            size="small"
                                            rowKey="id"
                                            columns={weightSalesCols}
                                            dataSource={weightSales}
                                            pagination={{ pageSize: 10 }}
                                            summary={(rows) => (
                                                <Table.Summary.Row>
                                                    <Table.Summary.Cell colSpan={1}>
                                                        <Text strong>Totals</Text>
                                                    </Table.Summary.Cell>
                                                    <Table.Summary.Cell align="right">
                                                        <Text strong>
                                                            {rows.reduce((s, r) => s + (r.weight_sold || 0), 0).toFixed(2)} kg
                                                        </Text>
                                                    </Table.Summary.Cell>
                                                    <Table.Summary.Cell />
                                                    <Table.Summary.Cell align="right">
                                                        <Text strong style={{ color: '#52c41a' }}>
                                                            {fmt(rows.reduce((s, r) => s + (r.total_amount || 0), 0))}
                                                        </Text>
                                                    </Table.Summary.Cell>
                                                    <Table.Summary.Cell colSpan={3} />
                                                </Table.Summary.Row>
                                            )}
                                        />
                                        <div style={{ marginTop: 12, padding: 12, background: '#f6ffed', borderRadius: 8 }}>
                                            <Text strong>Weight Summary:</Text>
                                            <div style={{ marginTop: 8 }}>
                                                <Row gutter={16}>
                                                    <Col span={8}>
                                                        <Statistic
                                                            title="Total Weight Sold"
                                                            value={totalWeightSold}
                                                            suffix="kg"
                                                            size="small"
                                                        />
                                                    </Col>
                                                    <Col span={8}>
                                                        <Statistic
                                                            title="Total Revenue"
                                                            value={totalWeightRevenue}
                                                            prefix="KES"
                                                            size="small"
                                                        />
                                                    </Col>
                                                    <Col span={8}>
                                                        <Statistic
                                                            title="Average Price/kg"
                                                            value={totalWeightSold ? totalWeightRevenue / totalWeightSold : 0}
                                                            prefix="KES"
                                                            precision={2}
                                                            size="small"
                                                        />
                                                    </Col>
                                                </Row>
                                            </div>
                                        </div>
                                    </>
                                )
                        },
                        
                        {
                            key: 'sales',
                            label: `Sales (${sales.length})`,
                            children:
                                sales.length === 0 ? (
                                    <Empty description="No sales" />
                                ) : (
                                    <Table
                                        size="small"
                                        rowKey={(_, i) => i}
                                        columns={[
                                            {
                                                title: 'Date',
                                                dataIndex: 'date',
                                                width: 100,
                                                render: (v) => (v ? dayjs(v).format('DD MMM YY') : '—')
                                            },
                                            { title: 'Description', dataIndex: 'description' },
                                            { title: 'Qty', dataIndex: 'quantity', width: 60, align: 'right' },
                                            {
                                                title: 'Unit Price',
                                                dataIndex: 'unitPrice',
                                                width: 90,
                                                align: 'right',
                                                render: (v) => formatKES(v)
                                            },
                                            {
                                                title: 'Total',
                                                dataIndex: 'amount',
                                                width: 100,
                                                align: 'right',
                                                render: (v) => (
                                                    <Text strong style={{ color: '#52c41a' }}>
                                                        {formatKES(v)}
                                                    </Text>
                                                )
                                            }
                                        ]}
                                        dataSource={sales}
                                        pagination={{ pageSize: 10 }}
                                        summary={(rows) => (
                                            <Table.Summary.Row>
                                                <Table.Summary.Cell colSpan={4}>
                                                    <Text strong>Total Revenue</Text>
                                                </Table.Summary.Cell>
                                                <Table.Summary.Cell align="right">
                                                    <Text strong style={{ color: '#52c41a' }}>
                                                        {formatKES(rows.reduce((s, r) => s + r.amount, 0))}
                                                    </Text>
                                                </Table.Summary.Cell>
                                            </Table.Summary.Row>
                                        )}
                                    />
                                )
                        },
                        ...(asset.is_batch || asset.type_slug === 'poultry'
                            ? [
                                  {
                                      key: 'eggs',
                                      label: `Eggs (${eggs.length})`,
                                      children:
                                          eggs.length === 0 ? (
                                              <Empty description="No egg collections" />
                                          ) : (
                                              <Table
                                                  size="small"
                                                  rowKey={(_, i) => i}
                                                  columns={[
                                                      {
                                                          title: 'Date',
                                                          dataIndex: 'date',
                                                          width: 100,
                                                          render: (v) => (v ? dayjs(v).format('DD MMM YYYY') : '—')
                                                      },
                                                      { title: 'Trays', dataIndex: 'traysCount', width: 70, align: 'right' },
                                                      { title: 'Eggs', dataIndex: 'eggsCount', width: 70, align: 'right' },
                                                      { title: 'Remarks', dataIndex: 'remarks', ellipsis: true }
                                                  ]}
                                                  dataSource={eggs}
                                                  pagination={{ pageSize: 10 }}
                                                  summary={(rows) => (
                                                      <Table.Summary.Row>
                                                          <Table.Summary.Cell>
                                                              <Text strong>Totals</Text>
                                                          </Table.Summary.Cell>
                                                          <Table.Summary.Cell align="right">
                                                              {rows.reduce((s, r) => s + (r.traysCount || 0), 0).toFixed(1)}
                                                          </Table.Summary.Cell>
                                                          <Table.Summary.Cell align="right">
                                                              {rows.reduce((s, r) => s + (r.eggsCount || 0), 0)}
                                                          </Table.Summary.Cell>
                                                          <Table.Summary.Cell colSpan={2} />
                                                      </Table.Summary.Row>
                                                  )}
                                              />
                                          )
                                  }
                              ]
                            : [])
                    ]}
                />
            </Spin>

            <ConsumptionHistoryModal
                assetId={assetId}
                assetName={displayName}
                open={consumptionModalOpen}
                onClose={() => setConsumptionModalOpen(false)}
            />
        </Drawer>
    );
};

const OffspringQueueDrawer = ({ open, onClose, farms, silos, assetTypes, subtypeMap, currentUser, onTagged }) => {
    const [queue, setQueue] = useState([]);
    const [loading, setLoading] = useState(false);
    const [tagModal, setTagModal] = useState(null);
    const [weanModal, setWeanModal] = useState(null);
    const [tagForm] = Form.useForm();
    const [weanForm] = Form.useForm();
    const [tagging, setTagging] = useState(false);
    const [weaning, setWeaning] = useState(false);

    // Filter states
    const [searchText, setSearchText] = useState('');
    const [filterType, setFilterType] = useState('all');

    const loadQueue = useCallback(() => {
        setLoading(true);
        apiFetch('/api/animal-assets/offspring-queue')
            .then((d) => setQueue(d.data || []))
            .catch((e) => message.error(e.message))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        if (open) loadQueue();
    }, [open, loadQueue]);

    // Apply filters
    const filteredQueue = queue.filter((item) => {
        // Filter by search text (parent tag or name)
        const matchesSearch =
            searchText === '' ||
            item.parentTag2?.toLowerCase().includes(searchText.toLowerCase()) ||
            item.animalName?.toLowerCase().includes(searchText.toLowerCase());

        // Filter by type
        const matchesType = filterType === 'all' || item.typeName === filterType;

        return matchesSearch && matchesType;
    });

    // Get unique types for filter dropdown
    const uniqueTypes = ['all', ...new Set(queue.map((item) => item.typeName).filter(Boolean))];

    const submitTag = async (v) => {
        setTagging(true);
        console.log('weane modal', tagModal);
        try {
            const d = await apiFetch(`/api/animal-assets/offspring-queue/${tagModal.id}/tag`, {
                method: 'POST',
                body: JSON.stringify({ ...v, userId: currentUser?.id, userName: currentUser?.firstname })
            });
            message.success(`${d.tagged?.length || 0} offspring tagged: ${d.tagged?.map((t) => t.tag).join(', ')}`);
            tagForm.resetFields();
            setTagModal(null);
            loadQueue();
            onTagged?.();
        } catch (e) {
            message.error(e.message);
        } finally {
            setTagging(false);
        }
    };

    const submitWean = async (v) => {
        setWeaning(true);
        console.log('weane modal', weanModal);

        try {
            const d = await apiFetch(`/api/animal-assets/offspring-queue/${weanModal.id}/wean`, {
                method: 'POST',
                body: JSON.stringify({ ...v, userId: currentUser?.id, userName: currentUser?.firstname })
            });
            message.success(`${d.weaned?.length || 0} offspring weaned: ${d.weaned?.map((t) => t.tag).join(', ')}`);
            weanForm.resetFields();
            setWeanModal(null);
            loadQueue();
            onTagged?.();
        } catch (e) {
            message.error(e.message);
        } finally {
            setWeaning(false);
        }
    };

    const cols = [
        {
            title: 'Parent',
            width: 70,
            render: (_, r) => (
                <Space direction="vertical" size={0}>
                    <Text style={{ fontFamily: 'monospace', color: '#1677ff', fontSize: 12 }}>{r.parentTag2}</Text>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                        {r.animalName}
                    </Text>
                </Space>
            )
        },
        { title: 'Type', dataIndex: 'typeName', width: 80, render: (v) => <Tag color="blue">{v}</Tag> },
        { title: 'Birth Date', dataIndex: 'birthDate', width: 100, render: (v) => dayjs(v).format('DD MMM YY') },
        { title: 'Born Alive', dataIndex: 'siredAlive', width: 80, align: 'right' },
        { title: 'Tagged', dataIndex: 'tagged', width: 70, align: 'right' },
        { title: 'Mortality', dataIndex: 'mortality', width: 70, align: 'right' },
        {
            title: 'Rem',
            width: 80,
            align: 'right',
            render: (_, r) => (
                <Badge count={(r.siredAlive || 0) - (r.tagged || 0) - (r.mortality || 0)} style={{ backgroundColor: '#fa8c16' }} />
            )
        },
        {
            title: 'Farm',
            dataIndex: 'farm',
            width: 80,
            render: (v) => {
                const farm = farms.find((f) => String(f.id) === String(v));
                return farm?.name || v;
            }
        },
        {
            title: '',
            width: 130,
            render: (_, r) => {
                const remaining = (r.siredAlive || 0) - (r.tagged || 0) - (r.mortality || 0);

                return (
                    <Space>
                        <Button
                            size="small"
                            type="primary"
                            icon={<TagOutlined />}
                            onClick={() => {
                                setTagModal(r);
                                tagForm.setFieldsValue({ count: remaining });
                            }}
                            disabled={remaining <= 0}
                        >
                            Tag
                        </Button>
                        <Button
                            size="small"
                            danger
                            icon={<MergeOutlined />}
                            onClick={() => {
                                setWeanModal(r);
                                weanForm.setFieldsValue({ count: remaining });
                            }}
                            disabled={remaining <= 0}
                        >
                            Wean
                        </Button>
                    </Space>
                );
            }
        }
    ];

    // Calculate summary
    const totalRemaining = filteredQueue.reduce((sum, r) => sum + ((r.siredAlive || 0) - (r.tagged || 0) - (r.mortality || 0)), 0);

    return (
        <Drawer
            title={
                <Space>
                    <TagOutlined />
                    Tag Offspring Queue
                    <Badge count={filteredQueue.length} color="#fa8c16" />
                    {totalRemaining > 0 && <Badge count={`${totalRemaining} remaining`} style={{ backgroundColor: '#52c41a' }} />}
                </Space>
            }
            open={open}
            onClose={onClose}
            width={820}
        >
            {/* Simple Filter Bar */}
            <div style={{ marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center' }}>
                <Input
                    placeholder="Search by parent tag..."
                    prefix={<SearchOutlined />}
                    allowClear
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    style={{ width: 200 }}
                />
                <Select placeholder="Filter by type" allowClear value={filterType} onChange={setFilterType} style={{ width: 150 }}>
                    {uniqueTypes.map((type) => (
                        <Option key={type} value={type}>
                            {type === 'all' ? 'All Types' : type}
                        </Option>
                    ))}
                </Select>
                {(searchText || filterType !== 'all') && (
                    <Button
                        size="small"
                        onClick={() => {
                            setSearchText('');
                            setFilterType('all');
                        }}
                    >
                        Clear Filters
                    </Button>
                )}
                <div style={{ marginLeft: 'auto' }}>
                    <Button icon={<ReloadOutlined />} size="small" onClick={loadQueue}>
                        Refresh
                    </Button>
                </div>
            </div>

            <Spin spinning={loading}>
                {filteredQueue.length === 0 ? (
                    <Empty
                        description={
                            queue.length === 0 ? 'No untagged offspring — all sired animals are tagged' : 'No records match your filters'
                        }
                    />
                ) : (
                    <>
                        <div style={{ marginBottom: 8, fontSize: 12, color: '#666' }}>
                            Showing {filteredQueue.length} of {queue.length} records
                        </div>
                        <Table
                            size="small"
                            rowKey="id"
                            columns={cols}
                            dataSource={filteredQueue}
                            pagination={{
                                pageSize: 20,
                                showSizeChanger: true,
                                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`
                            }}
                            scroll={{ x: 1000 }}
                        />
                    </>
                )}
            </Spin>

            {/* Tag Modal */}
            <Modal
                title={
                    tagModal && (
                        <Space>
                            <TagOutlined />
                            Tag Offspring — from <Text code>{tagModal.animalName || tagModal.parentTag2}</Text>
                        </Space>
                    )
                }
                open={!!tagModal}
                onCancel={() => {
                    setTagModal(null);
                    tagForm.resetFields();
                }}
                footer={null}
                destroyOnClose
            >
                {tagModal && (
                    <Form form={tagForm} layout="vertical" onFinish={submitTag}>
                        <Alert
                            type="info"
                            style={{ marginBottom: 12 }}
                            message={`${(tagModal.siredAlive || 0) - (tagModal.tagged || 0)} untagged. Each will get a sequential ${
                                assetTypes.find((t) => t.slug === tagModal.type_slug)?.prefix || ''
                            }—XXXXX tag.`}
                        />
                        <Row gutter={12}>
                            <Col span={8}>
                                <Form.Item
                                    name="count"
                                    label="How many to tag now"
                                    rules={[
                                        { required: true },
                                        { type: 'number', min: 1, max: (tagModal.siredAlive || 0) - (tagModal.tagged || 0) }
                                    ]}
                                >
                                    <InputNumber
                                        min={1}
                                        max={(tagModal.siredAlive || 0) - (tagModal.tagged || 0)}
                                        style={{ width: '100%' }}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item name="subtype_id" label="Subtype">
                                    <Select allowClear>
                                        {(subtypeMap[tagModal.type_slug] || []).map((s) => (
                                            <Option key={s.id} value={s.id}>
                                                {s.name}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item name="gender" label="Gender">
                                    <Select allowClear placeholder="Auto-detect">
                                        <Option value="male">Male</Option>
                                        <Option value="female">Female</Option>
                                        <Option value="unknown">Unknown</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item name="weight" label="Weight (kg)">
                                    <InputNumber min={0} step={0.1} style={{ width: '100%' }} />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item name="siloId" label="Silo">
                                    <Select allowClear>
                                        {(silos || [])
                                            .filter((s) => !s.type_slug || s.type_slug === tagModal.type_slug)
                                            .map((s) => (
                                                <Option key={s.id} value={s.id}>
                                                    {s.silo_number}
                                                </Option>
                                            ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Button type="primary" htmlType="submit" loading={tagging} block size="large">
                            Generate Tags & Add to Assets
                        </Button>
                    </Form>
                )}
            </Modal>

            {/* Wean Modal */}
            <Modal
                title={
                    weanModal && (
                        <Space>
                            <MergeOutlined />
                            Wean Offspring — from{' '}
                            <Text code>
                                {weanModal.animalName || weanModal.parentTag2} - {weanModal.id}
                            </Text>
                        </Space>
                    )
                }
                open={!!weanModal}
                onCancel={() => {
                    setWeanModal(null);
                    weanForm.resetFields();
                }}
                footer={null}
                destroyOnClose
            >
                {weanModal && (
                    <Form form={weanForm} layout="vertical" onFinish={submitWean}>
                        <Alert
                            type="warning"
                            style={{ marginBottom: 12 }}
                            message={`Weaning will generate a WEANER‑specific tag (separate sequence).`}
                        />
                        <Row gutter={12}>
                            <Col span={8}>
                                <Form.Item name="count" label="How many to wean now" rules={[{ required: true }]}>
                                    <InputNumber
                                        min={1}
                                        max={(weanModal.siredAlive || 0) - (weanModal.tagged || 0)}
                                        style={{ width: '100%' }}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item name="subtype_id" label="Subtype">
                                    <Select allowClear>
                                        {(subtypeMap[weanModal.type_slug] || []).map((s) => (
                                            <Option key={s.id} value={s.id}>
                                                {s.name}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item name="gender" label="Gender">
                                    <Select allowClear>
                                        <Option value="male">Male</Option>
                                        <Option value="female">Female</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item name="weight" label="Weight (kg)">
                                    <InputNumber min={0} step={0.1} style={{ width: '100%' }} />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item name="siloId" label="Silo">
                                    <Select allowClear>
                                        {(silos || [])
                                            .filter((s) => !s.type_slug || s.type_slug === weanModal.type_slug)
                                            .map((s) => (
                                                <Option key={s.id} value={s.id}>
                                                    {s.silo_number}
                                                </Option>
                                            ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Button type="primary" danger htmlType="submit" loading={weaning} block size="large">
                            Wean & Generate Weaner Tags
                        </Button>
                    </Form>
                )}
            </Modal>
        </Drawer>
    );
};

const ConfigModal = ({ open, onClose, onRefresh }) => {
    const [types, setTypes] = useState([]);
    const [subtypes, setSubtypes] = useState([]);
    const [tab, setTab] = useState('types');
    const [typeForm] = Form.useForm();
    const [subForm] = Form.useForm();
    const [saving, setSaving] = useState(false);
    const [editType, setEditType] = useState(null); // type object being edited
    const [editSub, setEditSub] = useState(null);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editSubModalOpen, setEditSubModalOpen] = useState(false);

    const loadAll = useCallback(async () => {
        const [tr, sr] = await Promise.all([apiFetch('/api/animal-assets/config/types'), apiFetch('/api/animal-assets/config/subtypes')]);
        setTypes(tr.data || []);
        setSubtypes(sr.data || []);
    }, []);

    useEffect(() => {
        if (open) loadAll();
    }, [open, loadAll]);

    // ─────────────────────────────────────────────────────────────
    // Asset Types
    // ─────────────────────────────────────────────────────────────
    const saveType = async (v) => {
        setSaving(true);
        try {
            await apiFetch('/api/animal-assets/config/types', { method: 'POST', body: JSON.stringify(v) });
            message.success('Type added');
            typeForm.resetFields();
            loadAll();
            onRefresh?.();
        } catch (e) {
            message.error(e.message);
        } finally {
            setSaving(false);
        }
    };

    const updateType = async (v) => {
        setSaving(true);
        try {
            await apiFetch(`/api/animal-assets/config/types/${editType.id}`, {
                method: 'PUT',
                body: JSON.stringify(v)
            });
            message.success('Type updated');
            setEditType(null);
            setEditModalOpen(false);
            loadAll();
            onRefresh?.();
        } catch (e) {
            message.error(e.message);
        } finally {
            setSaving(false);
        }
    };

    const deleteType = async (id, name) => {
        Modal.confirm({
            title: 'Delete Asset Type',
            content: `Are you sure you want to delete "${name}"? This may affect existing assets.`,
            okType: 'danger',
            onOk: async () => {
                try {
                    await apiFetch(`/api/animal-assets/config/types/${id}`, { method: 'DELETE' });
                    message.success('Type deleted');
                    loadAll();
                    onRefresh?.();
                } catch (e) {
                    message.error(e.message);
                }
            }
        });
    };

    // ─────────────────────────────────────────────────────────────
    // Subtypes
    // ─────────────────────────────────────────────────────────────
    const saveSub = async (v) => {
        setSaving(true);
        try {
            await apiFetch('/api/animal-assets/config/subtypes', { method: 'POST', body: JSON.stringify(v) });
            message.success('Subtype added');
            subForm.resetFields();
            loadAll();
        } catch (e) {
            message.error(e.message);
        } finally {
            setSaving(false);
        }
    };

    const updateSub = async (v) => {
        setSaving(true);
        try {
            await apiFetch(`/api/animal-assets/config/subtypes/${editSub.id}`, {
                method: 'PUT',
                body: JSON.stringify(v)
            });
            message.success('Subtype updated');
            setEditSub(null);
            setEditSubModalOpen(false);
            loadAll();
        } catch (e) {
            message.error(e.message);
        } finally {
            setSaving(false);
        }
    };

    const deleteSub = async (id, name) => {
        Modal.confirm({
            title: 'Delete Subtype',
            content: `Delete "${name}"? Existing assets using this subtype will be unaffected.`,
            okType: 'danger',
            onOk: async () => {
                try {
                    await apiFetch(`/api/animal-assets/config/subtypes/${id}`, { method: 'DELETE' });
                    message.success('Subtype deleted');
                    loadAll();
                } catch (e) {
                    message.error(e.message);
                }
            }
        });
    };

    // ─────────────────────────────────────────────────────────────
    // Table columns with actions
    // ─────────────────────────────────────────────────────────────
    const typeColumns = [
        { title: 'Name', dataIndex: 'name', width: 120 },
        { title: 'Slug', dataIndex: 'slug', width: 100, render: (v) => <Text code>{v}</Text> },
        { title: 'Prefix', dataIndex: 'prefix', width: 65 },
        {
            title: 'Tag Format',
            dataIndex: 'tag_format',
            width: 140,
            render: (v) => (
                <Text code style={{ fontSize: 11 }}>
                    {v}
                </Text>
            )
        },
        { title: 'Category', dataIndex: 'category', width: 90 },
        { title: 'Batch', dataIndex: 'is_batch', width: 60, render: (v) => (v ? <Tag color="blue">Yes</Tag> : '—') },
        { title: 'Silo', dataIndex: 'has_silo', width: 55, render: (v) => (v ? <Tag color="green">Yes</Tag> : '—') },
        { title: 'GL Dr', dataIndex: 'default_gl_debit', width: 65 },
        { title: 'GL Cr', dataIndex: 'default_gl_credit', width: 65 },
        { title: 'Depr.', dataIndex: 'depreciation_method', width: 130 },
        { title: 'Life', dataIndex: 'default_life_years', width: 55, render: (v) => (v ? `${v}y` : '—') },
        { title: 'Active', dataIndex: 'active', width: 65, render: (v) => (v ? <Tag color="green">Yes</Tag> : <Tag>No</Tag>) },
        {
            title: 'Actions',
            width: 100,
            fixed: 'right',
            render: (_, record) => (
                <Space>
                    <Button
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => {
                            setEditType(record);
                            setEditModalOpen(true);
                        }}
                    />
                    <Button size="small" danger icon={<DeleteOutlined />} onClick={() => deleteType(record.id, record.name)} />
                </Space>
            )
        }
    ];

    const subtypeColumns = [
        { title: 'Type', dataIndex: 'type_name', width: 120 },
        { title: 'Subtype', dataIndex: 'name', width: 140 },
        { title: 'Code', dataIndex: 'code', width: 80, render: (v) => <Text code>{v || '—'}</Text> },
        { title: 'Active', dataIndex: 'active', width: 70, render: (v) => (v ? <Tag color="green">Yes</Tag> : <Tag>No</Tag>) },
        {
            title: 'Actions',
            width: 100,
            render: (_, record) => (
                <Space>
                    <Button
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => {
                            setEditSub(record);
                            setEditSubModalOpen(true);
                        }}
                    />
                    <Button size="small" danger icon={<DeleteOutlined />} onClick={() => deleteSub(record.id, record.name)} />
                </Space>
            )
        }
    ];

    return (
        <Modal
            title={
                <Space>
                    <SettingOutlined /> Asset Configuration
                </Space>
            }
            open={open}
            onCancel={onClose}
            width={1100}
            footer={null}
            destroyOnClose
        >
            <Tabs
                activeKey={tab}
                onChange={setTab}
                items={[
                    {
                        key: 'types',
                        label: 'Asset Types / Species',
                        children: (
                            <>
                                <Table
                                    size="small"
                                    rowKey="id"
                                    pagination={false}
                                    scroll={{ x: 1200 }}
                                    dataSource={types}
                                    columns={typeColumns}
                                    style={{ marginBottom: 20 }}
                                />
                                <Divider orientation="left">Add New Type</Divider>
                                <Form form={typeForm} layout="vertical" onFinish={saveType}>
                                    <Row gutter={10}>
                                        <Col span={3}>
                                            <Form.Item
                                                name="category"
                                                label="Category"
                                                rules={[{ required: true }]}
                                                initialValue="livestock"
                                            >
                                                <Select>
                                                    <Option value="livestock">Livestock</Option>
                                                    <Option value="physical">Physical</Option>
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                        <Col span={4}>
                                            <Form.Item name="name" label="Name" rules={[{ required: true }]}>
                                                <Input placeholder="e.g. Rabbit" />
                                            </Form.Item>
                                        </Col>
                                        <Col span={3}>
                                            <Form.Item name="slug" label="Slug" rules={[{ required: true }]}>
                                                <Input placeholder="rabbit" />
                                            </Form.Item>
                                        </Col>
                                        <Col span={2}>
                                            <Form.Item name="prefix" label="Prefix" rules={[{ required: true }]}>
                                                <Input placeholder="RB" />
                                            </Form.Item>
                                        </Col>
                                        <Col span={4}>
                                            <Form.Item name="tag_format" label="Tag Format">
                                                <Input placeholder="{PREFIX}-{SEQ5}" />
                                            </Form.Item>
                                        </Col>
                                        <Col span={2}>
                                            <Form.Item name="color" label="Color">
                                                <Input placeholder="green" />
                                            </Form.Item>
                                        </Col>
                                        <Col span={3}>
                                            <Form.Item name="default_gl_debit" label="GL Debit">
                                                <Input />
                                            </Form.Item>
                                        </Col>
                                        <Col span={3}>
                                            <Form.Item name="default_gl_credit" label="GL Credit">
                                                <Input />
                                            </Form.Item>
                                        </Col>
                                        <Col span={4}>
                                            <Form.Item name="depreciation_method" label="Depreciation" initialValue="none">
                                                <Select>
                                                    <Option value="none">None</Option>
                                                    <Option value="straight_line">Straight Line</Option>
                                                    <Option value="declining_balance">Declining</Option>
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                        <Col span={2}>
                                            <Form.Item name="default_life_years" label="Life (y)">
                                                <InputNumber min={0} style={{ width: '100%' }} />
                                            </Form.Item>
                                        </Col>
                                        <Col span={2}>
                                            <Form.Item name="is_batch" label="Batch?" initialValue={0}>
                                                <Select>
                                                    <Option value={0}>No</Option>
                                                    <Option value={1}>Yes</Option>
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                        <Col span={2}>
                                            <Form.Item name="has_silo" label="Silo?" initialValue={0}>
                                                <Select>
                                                    <Option value={0}>No</Option>
                                                    <Option value={1}>Yes</Option>
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                        <Col span={24} style={{ textAlign: 'right' }}>
                                            <Button type="primary" htmlType="submit" loading={saving} icon={<PlusOutlined />}>
                                                Add Type
                                            </Button>
                                        </Col>
                                    </Row>
                                </Form>
                            </>
                        )
                    },
                    {
                        key: 'subtypes',
                        label: 'Subtypes / Breeds',
                        children: (
                            <>
                                <Table
                                    size="small"
                                    rowKey="id"
                                    pagination={{ pageSize: 15 }}
                                    dataSource={subtypes}
                                    columns={subtypeColumns}
                                    style={{ marginBottom: 20 }}
                                />
                                <Divider orientation="left">Add Subtype</Divider>
                                <Form form={subForm} layout="vertical" onFinish={saveSub}>
                                    <Row gutter={12}>
                                        <Col span={8}>
                                            <Form.Item name="type_id" label="Type" rules={[{ required: true }]}>
                                                <Select showSearch>
                                                    {types.map((t) => (
                                                        <Option key={t.id} value={t.id}>
                                                            <Text>{t.name}</Text>
                                                        </Option>
                                                    ))}
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                        <Col span={8}>
                                            <Form.Item name="name" label="Subtype Name" rules={[{ required: true }]}>
                                                <Input />
                                            </Form.Item>
                                        </Col>
                                        <Col span={4}>
                                            <Form.Item name="code" label="Code">
                                                <Input />
                                            </Form.Item>
                                        </Col>
                                        <Col span={4} style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 24 }}>
                                            <Button type="primary" htmlType="submit" loading={saving} icon={<PlusOutlined />} block>
                                                Add
                                            </Button>
                                        </Col>
                                    </Row>
                                </Form>
                            </>
                        )
                    }
                ]}
            />

            {/* Edit Type Modal */}
            <Modal title="Edit Asset Type" open={editModalOpen} onCancel={() => setEditModalOpen(false)} footer={null} destroyOnClose>
                {editType && (
                    <Form initialValues={editType} onFinish={updateType} layout="vertical">
                        {/* same fields as in add form, but pre-filled */}
                        <Row gutter={10}>
                            <Col span={6}>
                                <Form.Item name="category" label="Category" rules={[{ required: true }]}>
                                    <Select>
                                        <Option value="livestock">Livestock</Option>
                                        <Option value="physical">Physical</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={6}>
                                <Form.Item name="name" label="Name" rules={[{ required: true }]}>
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col span={6}>
                                <Form.Item name="slug" label="Slug" rules={[{ required: true }]}>
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col span={6}>
                                <Form.Item name="prefix" label="Prefix" rules={[{ required: true }]}>
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item name="tag_format" label="Tag Format">
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col span={4}>
                                <Form.Item name="default_gl_debit" label="GL Debit">
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col span={4}>
                                <Form.Item name="default_gl_credit" label="GL Credit">
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col span={4}>
                                <Form.Item name="depreciation_method" label="Depreciation">
                                    <Select>
                                        <Option value="none">None</Option>
                                        <Option value="straight_line">Straight Line</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={4}>
                                <Form.Item name="default_life_years" label="Life (y)">
                                    <InputNumber min={0} style={{ width: '100%' }} />
                                </Form.Item>
                            </Col>
                            <Col span={4}>
                                <Form.Item name="is_batch" label="Batch?" initialValue={0}>
                                    <Select>
                                        <Option value={0}>No</Option>
                                        <Option value={1}>Yes</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={4}>
                                <Form.Item name="has_silo" label="Silo?" initialValue={0}>
                                    <Select>
                                        <Option value={0}>No</Option>
                                        <Option value={1}>Yes</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={4}>
                                <Form.Item name="active" label="Active" initialValue={1}>
                                    <Select>
                                        <Option value={1}>Yes</Option>
                                        <Option value={0}>No</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>
                        <div style={{ textAlign: 'right' }}>
                            <Button type="primary" htmlType="submit" loading={saving}>
                                Update
                            </Button>
                        </div>
                    </Form>
                )}
            </Modal>

            {/* Edit Subtype Modal */}
            <Modal title="Edit Subtype" open={editSubModalOpen} onCancel={() => setEditSubModalOpen(false)} footer={null} destroyOnClose>
                {editSub && (
                    <Form initialValues={editSub} onFinish={updateSub} layout="vertical">
                        <Form.Item name="type_id" label="Type" rules={[{ required: true }]}>
                            <Select showSearch>
                                {types.map((t) => (
                                    <Option key={t.id} value={t.id}>
                                        {t.name}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Form.Item name="name" label="Subtype Name" rules={[{ required: true }]}>
                            <Input />
                        </Form.Item>
                        <Form.Item name="code" label="Code">
                            <Input />
                        </Form.Item>
                        <Form.Item name="active" label="Active" initialValue={1}>
                            <Select>
                                <Option value={1}>Yes</Option>
                                <Option value={0}>No</Option>
                            </Select>
                        </Form.Item>
                        <div style={{ textAlign: 'right' }}>
                            <Button type="primary" htmlType="submit" loading={saving}>
                                Update
                            </Button>
                        </div>
                    </Form>
                )}
            </Modal>
        </Modal>
    );
};

const LineRow = ({ item, idx, onChange, onRemove, farms, silos, assetTypes, subtypeMap }) => {
    const cfg = assetTypes.find((t) => t.slug === item.type_slug) || {};
    const subtypes = subtypeMap[item.type_slug] || [];
    const up = (f, v) => onChange(idx, { ...item, [f]: v });
    const qty = cfg.is_batch ? item.quantity || 1 : 1;
    const total = (qty * (item.cost || 0)).toFixed(2);

    return (
        <tr style={{ borderBottom: '1px solid #f5f5f5', verticalAlign: 'middle' }}>
            {/* Type */}
            <td style={{ padding: '5px 4px', minWidth: 120 }}>
                <Select
                    size="small"
                    value={item.type_slug}
                    style={{ width: '100%' }}
                    placeholder="Type"
                    onChange={(v) => {
                        const c = assetTypes.find((t) => t.slug === v) || {};
                        onChange(idx, {
                            ...item,
                            type_slug: v,
                            subtype_id: undefined,
                            category: c.is_livestock !== false ? 'livestock' : 'physical',
                            tagNumber: null,
                            batchId: null,
                            gender: null
                        });
                    }}
                >
                    {assetTypes.map((t) => (
                        <Option key={t.slug} value={t.slug}>
                            <Text>{t.name}</Text>
                        </Option>
                    ))}
                </Select>
            </td>

            {/* Subtype */}
            <td style={{ padding: '5px 4px', minWidth: 110 }}>
                <Select
                    size="small"
                    value={item.subtype_id}
                    style={{ width: '100%' }}
                    placeholder="Subtype"
                    disabled={!item.type_slug}
                    onChange={(v) => {
                        // Gender will be inferred by backend – no need to set here
                        onChange(idx, { ...item, subtype_id: v });
                    }}
                    allowClear
                >
                    {subtypes.map((s) => (
                        <Option key={s.id} value={s.id}>
                            {s.name}
                        </Option>
                    ))}
                </Select>
            </td>

            {/* Name (optional) */}
            <td style={{ padding: '5px 4px', minWidth: 110 }}>
                <Input size="small" value={item.animalName} onChange={(e) => up('animalName', e.target.value)} placeholder="Name" />
            </td>

            {/* Quantity (batch only) */}
            <td style={{ padding: '5px 4px', minWidth: 60 }}>
                {cfg.is_batch ? (
                    <InputNumber
                        size="small"
                        min={1}
                        value={item.quantity}
                        onChange={(v) => onChange(idx, { ...item, quantity: v, totalCost: (v || 1) * (item.cost || 0) })}
                        style={{ width: '100%' }}
                    />
                ) : (
                    <Input size="small" value="1" disabled style={{ width: '100%', textAlign: 'center', background: '#fafafa' }} />
                )}
            </td>

            {/* Unit Cost */}
            <td style={{ padding: '5px 4px', minWidth: 95 }}>
                <InputNumber
                    size="small"
                    min={0}
                    value={item.cost}
                    onChange={(v) => onChange(idx, { ...item, cost: v, totalCost: qty * (v || 0) })}
                    style={{ width: '100%' }}
                    formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                />
            </td>

            {/* Total */}
            <td style={{ padding: '5px 4px', minWidth: 95 }}>
                <InputNumber
                    size="small"
                    disabled
                    value={total}
                    style={{ width: '100%', background: '#fafafa' }}
                    formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                />
            </td>

            {/* Birth date (only livestock) */}
            {cfg.is_livestock !== false && (
                <td style={{ padding: '5px 4px', minWidth: 100 }}>
                    <DatePicker
                        size="small"
                        value={item.birth ? dayjs(item.birth) : null}
                        onChange={(d) => up('birth', d ? d.format('YYYY-MM-DD') : null)}
                        style={{ width: '100%' }}
                    />
                </td>
            )}

            {/* Silo (only livestock with silo) */}
            {cfg.is_livestock !== false && cfg.has_silo && (
                <td style={{ padding: '5px 4px', minWidth: 90 }}>
                    <Select size="small" value={item.siloId} onChange={(v) => up('siloId', v)} style={{ width: '100%' }} allowClear>
                        {(silos || [])
                            .filter((s) => !s.type_slug || s.type_slug === item.type_slug)
                            .map((s) => (
                                <Option key={s.id} value={s.id}>
                                    {s.silo_number}
                                </Option>
                            ))}
                    </Select>
                </td>
            )}

            {/* Physical asset fields */}
            {cfg.is_livestock === false && (
                <>
                    <td style={{ padding: '5px 4px', minWidth: 120 }}>
                        <Input
                            size="small"
                            value={item.assetName}
                            onChange={(e) => up('assetName', e.target.value)}
                            placeholder="Asset name"
                        />
                    </td>
                    <td style={{ padding: '5px 4px', minWidth: 100 }}>
                        <Input
                            size="small"
                            value={item.serialNumber}
                            onChange={(e) => up('serialNumber', e.target.value)}
                            placeholder="Serial #"
                        />
                    </td>
                    <td style={{ padding: '5px 4px', minWidth: 75 }}>
                        <InputNumber
                            size="small"
                            min={0}
                            value={item.usefulLifeYears}
                            onChange={(v) => up('usefulLifeYears', v)}
                            style={{ width: '100%' }}
                            placeholder="Life"
                        />
                    </td>
                </>
            )}

            {/* Farm */}
            <td style={{ padding: '5px 4px', minWidth: 85 }}>
                <Select size="small" value={item.farm} onChange={(v) => up('farm', v)} style={{ width: '100%' }} allowClear>
                    {(farms || []).map((f) => (
                        <Option key={f.id} value={String(f.id)}>
                            {f.name}
                        </Option>
                    ))}
                </Select>
            </td>

            {/* Delete */}
            <td style={{ padding: '5px 4px' }}>
                <Button size="small" danger type="text" icon={<DeleteOutlined />} onClick={() => onRemove(idx)} />
            </td>
        </tr>
    );
};

// =============================================================================
// EXPORT HELPER
// =============================================================================
// const doExport = (path, filename) => {
//     const a = document.createElement('a');
//     a.href = `${API}${path}`;
//     a.download = filename;
//     a.click();
// };

const doExport = async (path, filename) => {
    try {
        const response = await api.get(path, {
            responseType: 'blob'
        });

        const blob = new Blob([response.data]);
        const url = window.URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = filename;

        document.body.appendChild(a);
        a.click();
        a.remove();

        window.URL.revokeObjectURL(url);
    } catch (error) {
        console.error('Export failed:', error);

        if (error.response?.status === 401) {
            message.error('Unauthorized. Please log in again.');
        } else {
            message.error('Failed to export file.');
        }
    }
};

// =============================================================================
// MAIN PAGE
// =============================================================================
const AnimalAssetsPage = () => {
    const invoiceRef = useRef();
    const assetRef = useRef();
    const [users, setUsers] = useState([]);
    const currentUser = useSelector((state) => state.account.user);

    const [assetTypes, setAssetTypes] = useState([]);
    const [subtypeMap, setSubtypeMap] = useState({});
    const [farms, setFarms] = useState([]);
    const [silos, setSilos] = useState([]);
    const [stakeholders, setStakeholders] = useState([]);

    // invoice state
    const [invoiceOpen, setInvoiceOpen] = useState(false);
    const [headerForm] = Form.useForm();
    const [lineItems, setLineItems] = useState([]);
    const [saving, setSaving] = useState(false);
    const [payStatus, setPayStatus] = useState('unpaid');

    // drawer
    const [drawerId, setDrawerId] = useState(null);
    const [drawerTab, setDrawerTab] = useState('items');
    const [drawerOpen, setDrawerOpen] = useState(false);

    // bulk
    const [selectedKeys, setSelectedKeys] = useState([]);
    const [bulkAction, setBulkAction] = useState(null);
    const [bulkModal, setBulkModal] = useState(false);
    const [bulkLoading, setBulkLoading] = useState(false);
    const [bulkResult, setBulkResult] = useState(null);

    // row action
    const [rowAction, setRowAction] = useState(null);
    const [rowModal, setRowModal] = useState(false);
    const [rowLoading, setRowLoading] = useState(false);

    // statement
    const [statId, setStatId] = useState(null);
    const [statOpen, setStatOpen] = useState(false);

    // config
    const [configOpen, setConfigOpen] = useState(false);

    // tabs
    const [mainTab, setMainTab] = useState('headers');
    const [assetSubTab, setAssetSubTab] = useState('all');

    const [supplierMap, setSupplierMap] = useState({});
    const [queueCount, setQueueCount] = useState(0);
    const [queueOpen, setQueueOpen] = useState(false);

    // filters
    const [invoiceFilters, setInvoiceFilters] = useState({});
    const [assetFilters, setAssetFilters] = useState({});
    const [paidAccounts, setPaidAccounts] = useState([]);

    const updateInvFilter = (k, v) =>
        setInvoiceFilters((p) => (v !== undefined ? { ...p, [k]: v } : Object.fromEntries(Object.entries(p).filter(([key]) => key !== k))));
    const updateAstFilter = (k, v) =>
        setAssetFilters((p) => (v !== undefined ? { ...p, [k]: v } : Object.fromEntries(Object.entries(p).filter(([key]) => key !== k))));
    // Add these with your other state declarations
    const [reversalModalVisible, setReversalModalVisible] = useState(false);
    const [selectedForReversal, setSelectedForReversal] = useState(null);
    const [reversalSuccess, setReversalSuccess] = useState(false);
    // Add these to your existing state declarations
    const [editModalVisible, setEditModalVisible] = useState(false);
    // const [reversalModalVisible, setReversalModalVisible] = useState(false);
    const [selectedAsset, setSelectedAsset] = useState(null);
    const [stakeholderModalVisible, setStakeholderModalVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [selectedStakeholder, setSelectedStakeholder] = useState(null);

    // Add this function in your AnimalAssetsPage component
    const openReversalModal = (record) => {
        // console.log('show rec', record);
        setSelectedForReversal(record);
        setReversalModalVisible(true);
    };

    const handleReversalSuccess = () => {
        setReversalSuccess(true);
        // Reload both tables to show updated data
        invoiceRef.current?.reload();
        assetRef.current?.reload();
        loadConfig(); // Reload config to update queue counts
        message.success('Transaction reversed successfully');
    };

    // Load static data for filters
    useEffect(() => {
        const loadFilters = async () => {
            try {
                const [userData] = await Promise.all([fetchUsers()]);
                setUsers(userData);
            } catch (error) {
                console.error(error);
            }
        };
        loadFilters();
    }, []);

    // Fetch paid-through accounts for the form (used in select options)
    const fetchPaidAccounts = useCallback(async () => {
        try {
            const { data } = await api.get(`/api/paid-through`);
            setPaidAccounts(data);
        } catch (error) {
            console.error('Failed to fetch paid-through accounts', error);
        }
    }, [API]);

    useEffect(() => {
        fetchPaidAccounts();
    }, [fetchPaidAccounts]);

    // Prepare select options (grouped) for the form
    const paidOptions = groupAccountOptions(paidAccounts); // from your utils

    // config load
    const loadConfig = useCallback(async () => {
        try {
            const [tr, fr, sr, shr, subr, qr] = await Promise.all([
                apiFetch('/api/animal-assets/config/types'),
                apiFetch('/farm'),
                apiFetch('/api/animal-assets/config/silos'),
                apiFetch('/suppliers'),
                apiFetch('/api/animal-assets/config/subtypes'),
                apiFetch('/api/animal-assets/offspring-queue')
            ]);
            setAssetTypes(tr.data || []);
            setFarms(fr || []);
            setSilos(sr.data || []);
            setStakeholders(shr || []);
            const supplierMap = {};
            (shr || []).forEach((s) => {
                supplierMap[s.id] = s.name;
            });
            setSupplierMap(supplierMap);
            const map = {};
            (subr.data || []).forEach((s) => {
                if (!map[s.slug]) map[s.slug] = [];
                map[s.slug].push(s);
            });
            setSubtypeMap(map);
            setQueueCount((qr.data || []).length);
        } catch (e) {
            message.error('Config load failed');
        }
    }, []);

    useEffect(() => {
        loadConfig();
    }, [loadConfig]);

    const subtotal = lineItems.reduce((s, r) => {
        const cfg = assetTypes.find((t) => t.slug === r.type_slug) || {};
        return s + (cfg.is_batch ? r.quantity || 1 : 1) * (r.cost || 0);
    }, 0);

    const previewTag = useCallback(async (slug, farmId) => {
        const d = await apiFetch(`/api/animal-assets/next-tag?slug=${slug}${farmId ? `&farm_id=${farmId}` : ''}`);
        return d.tag;
    }, []);

    // Handle new stakeholder added
    const handleStakeholderAdded = (newStakeholder) => {
        // console.log(newStakeholder, "sho ne st");

        setStakeholders((prev) => [...prev, newStakeholder]);

        // force select update
        setSelectedStakeholder(newStakeholder.id);

        headerForm.setFieldsValue({
            stakeholderId: newStakeholder.id
        });

        message.success(`${newStakeholder.name} added and selected`);
    };

    const addRow = async (slug) => {
        const cfg = assetTypes.find((t) => t.slug === slug) || {};
        setLineItems((p) => [
            ...p,
            {
                _key: genKey(),
                type_slug: slug || null,
                subtype_id: null,
                category: cfg.is_livestock !== false ? 'livestock' : 'physical',
                animalName: '',
                // Tag / Batch removed – backend will generate
                tagNumber: null,
                batchId: null,
                quantity: 1,
                cost: 0,
                totalCost: 0,
                // Gender removed – backend infers from subtype
                gender: null,
                birth: null,
                farm: null,
                siloId: null,
                assetName: '',
                serialNumber: '',
                usefulLifeYears: cfg.default_life_years || null
            }
        ]);
    };

    const updateRow = (i, v) =>
        setLineItems((p) =>
            p.map((r, idx) => {
                if (idx !== i) return r;
                const cfg = assetTypes.find((t) => t.slug === v.type_slug) || {};
                return { ...v, totalCost: (cfg.is_batch ? v.quantity || 1 : 1) * (v.cost || 0) };
            })
        );

    const removeRow = (i) => setLineItems((p) => p.filter((_, idx) => idx !== i));

    const saveInvoice = async () => {
        if (!lineItems.length) {
            message.error('Add at least one line item.');
            return;
        }
        if (lineItems.some((r) => !r.type_slug || !r.cost)) {
            message.error('Each item needs type and cost.');
            return;
        }
        try {
            const header = await headerForm.validateFields();
            setSaving(true);
            const d = await apiFetch('/api/animal-assets', {
                method: 'POST',
                body: JSON.stringify({
                    header: {
                        ...header,
                        recorded: header.recorded?.format('YYYY-MM-DD'),
                        dueDate: header.dueDate?.format('YYYY-MM-DD'),
                        userId: currentUser?.id,
                        userName: currentUser?.name
                    },
                    items: lineItems.map((r) => {
                        const cfg = assetTypes.find((t) => t.slug === r.type_slug) || {};
                        return { ...r, totalCost: (cfg.is_batch ? r.quantity || 1 : 1) * (r.cost || 0) };
                    })
                })
            });
            message.success(
                `Invoice ${d.transactionId} saved — ${d.insertedIds.length} asset(s). Tags: ${d.insertedIds.map((i) => i.tag).join(', ')}`
            );
            headerForm.resetFields();
            setLineItems([]);
            setInvoiceOpen(false);
            invoiceRef.current?.reload();
            assetRef.current?.reload();
            loadConfig();
        } catch (e) {
            message.error(e.message);
        } finally {
            setSaving(false);
        }
    };

    const submitBulk = async (payload, form) => {
        setBulkLoading(true);
        try {
            const d = await apiFetch('/api/animal-assets/bulk-action', {
                method: 'POST',
                body: JSON.stringify({
                    action: bulkAction,
                    ids: selectedKeys,
                    payload,
                    userId: currentUser?.id,
                    userName: currentUser?.firstname
                })
            });
            setBulkResult(d);
            form.resetFields();
            assetRef.current?.reload();
            setSelectedKeys([]);
            if (bulkAction === 'birth') loadConfig(); // refresh queue badge
        } catch (e) {
            message.error(e.message);
        } finally {
            setBulkLoading(false);
        }
    };

    const submitRowAction = async (payload, form) => {
        setRowLoading(true);
        try {
            await apiFetch('/api/animal-assets/bulk-action', {
                method: 'POST',
                body: JSON.stringify({
                    action: rowAction.action,
                    ids: [rowAction.record.id],
                    payload,
                    userId: currentUser?.id,
                    userName: currentUser?.name
                })
            });
            message.success('Applied.');
            form.resetFields();
            setRowModal(false);
            assetRef.current?.reload();
            if (rowAction.action === 'birth') loadConfig();
        } catch (e) {
            message.error(e.message);
        } finally {
            setRowLoading(false);
        }
    };

    const rowMenu = (record) => ({
        items: [
            {
                key: 'statement',
                label: (
                    <Space>
                        <BarChartOutlined />
                        Statement / P&L
                    </Space>
                )
            },
            { type: 'divider' },
            ...ACTIONS.filter(
                (action) =>
                    // Check if the action's types array includes the record's category
                    action.types.includes(record.category) // or record.asset_category, depending on your data structure
            ).map((a) => ({
                key: a.key,
                label: (
                    <Space>
                        {a.icon}
                        <span>{a.label}</span>
                    </Space>
                )
            }))
        ],
        onClick: ({ key }) => {
            if (key === 'statement') {
                setStatId(record.id);
                setStatOpen(true);
                return;
            }
            setRowAction({ action: key, record });
            setRowModal(true);
        }
    });


    // ── invoice columns ──────────────────────────────────────────────────────
    const invoiceCols = [
        { title: 'Date', dataIndex: 'recorded', width: 100, render: (v) => (v ? dayjs(v).format('DD MMM YYYY') : '—'), sorter: true },
        {
            title: 'Transaction ID',
            dataIndex: 'transactionId',
            // width: 180,
            copyable: true
        },
        {
            title: 'Stakeholder',
            dataIndex: 'stakeholderId',
            // width: 100,
            render: (v) => (v ? <Link to={`/stakeholder/details/${v}`}>{supplierMap[v] || '—'}</Link> : '—')
        },
        {
            title: 'Items',
            dataIndex: 'lineCount',
            // width: 60,
            align: 'right'
        },
        {
            title: 'Total',
            dataIndex: 'totalCost',
            //  width: 120,
            align: 'right',
            render: (v) => <Text>{fmt(v)}</Text>,
            sorter: true
        },
        {
            title: 'Paid',
            dataIndex: 'amountPaidTotal',
            // width: 110,
            align: 'right',
            render: (v) => <Text>{fmt(v)}</Text>
        },
        {
            title: 'Outstanding',
            dataIndex: 'outstanding',
            // width: 120,
            align: 'right',
            render: (v) => (parseFloat(v) > 0 ? <Text>{fmt(v)}</Text> : <Tag color="green">Cleared</Tag>)
        },
        {
            title: 'Status',
            dataIndex: 'paymentStatus',
            filters: true,
            valueEnum: {
                fully_paid: { text: 'Paid', status: 'Success' },
                unpaid: { text: 'Unpaid', status: 'Error' },
                partially_paid: { text: 'Partial', status: 'Warning' },
                cash: { text: 'Cash', status: 'Warning' }
            }
        },

        {
            title: 'Actions',
            // width: 150,
            fixed: 'right',
            render: (_, r) => (
                <Space>
                    <Button
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => {
                            setDrawerId(r.id);
                            setDrawerTab('items');
                            setDrawerOpen(true);
                        }}
                    >
                        {/* View */}
                    </Button>
                    <Button
                        size="small"
                        type="primary"
                        ghost
                        icon={<DollarOutlined />}
                        onClick={() => {
                            setDrawerId(r.id);
                            setDrawerTab('payments');
                            setDrawerOpen(true);
                        }}
                    >
                        {/* Pay */}
                    </Button>
                    {!r.reversed && (
                        <Tooltip title="Reverse Invoice">
                            <Button icon={<RollbackOutlined />} size="small" danger onClick={() => openReversalModal(r)} />
                        </Tooltip>
                    )}
                </Space>
            )
        }
    ];

    // ── asset columns ────────────────────────────────────────────────────────
    const assetCols = [
        {
            title: 'Type',
            align: 'left',
            render: (_, r) => <Text>{r.typeName || r.type_slug}</Text>
        },
        {
            title: 'Tag',
            align: 'left',
            render: (_, r) => (
                <Text copyable={{ text: r.tagNumber || r.batchId || r.assetName || '' }}>
                    {r.tagNumber || r.batchId || r.assetName || '—'}
                </Text>
            )
        },
        { title: 'Subtype', dataIndex: 'subtypeName', width: 90, render: (v) => v || '—' },

        { title: 'Qty', dataIndex: 'quantity', width: 55, align: 'right' },
        { title: 'Cost', dataIndex: 'totalCost', width: 110, align: 'right', sorter: true, render: (v) => fmt(v) },
        {
            title: 'Value',
            dataIndex: 'currentValue',
            align: 'right',
            render: (v, r) => {
                const diff = parseFloat(v || 0) - parseFloat(r.totalCost || 0);
                return (
                    <Space size={2}>
                        <Text strong>{fmt(v)}</Text>
                        {diff !== 0 && (
                            <Text style={{ fontSize: 10, color: diff > 0 ? '#52c41a' : '#f5222d' }}>
                                {diff > 0 ? '+' : ''}
                                {fmt(diff)}
                            </Text>
                        )}
                    </Space>
                );
            }
        },
        {
            title: 'Status',
            dataIndex: 'status',
            // width: 100,
            filters: true,
            valueEnum: {
                alive: { text: 'Alive', status: 'Success' },
                active: { text: 'Active', status: 'Success' },
                sold: { text: 'Sold', status: 'Warning' },
                weaned: { text: 'Weaned', status: 'Warning' },
                mortality: { text: 'Mortality', status: 'Error' }
            }
        },
        // Add to your asset table columns
        {
            title: 'Weight',
            dataIndex: 'current_weight',
            key: 'weight_status',
            width: 120,
            render: (weight, record) => {
                if (record.type_slug === 'poultry') return '-';
                const currentWt = record.current_weight || record.weight || 0;
                const soldWt = record.weight_sold_total || 0;
                if (currentWt > 0 && soldWt > 0) {
                    return (
                        <Tooltip title={`Sold: ${soldWt}kg, Remaining: ${currentWt}kg`}>
                            <Tag color="orange">{currentWt} kg remaining</Tag>
                        </Tooltip>
                    );
                }
                return currentWt > 0 ? `${currentWt} kg` : '-';
            }
        },
        {
            title: 'Farm',
            dataIndex: 'farmName' // width: 80
        },
        {
            title: 'Silo',
            dataIndex: 'siloNumDisplay', // width: 70,
            render: (v) => v || '—'
        },
        {
            title: 'Date',
            dataIndex: 'recorded', // width: 100,
            sorter: true,
            render: (v) => (v ? dayjs(v).format('DD MMM YY') : '—')
        },

        {
            title: '',
            width: 150,
            fixed: 'right',
            render: (_, r) => (
                <Space>
                    {/* View Details */}
                    <Tooltip title="View Details">
                        <Button
                            icon={<EyeOutlined />}
                            size="small"
                            onClick={() => {
                                setStatId(r.id);
                                setStatOpen(true);
                            }}
                        />
                    </Tooltip>

                    {/* Edit Asset */}
                    <Tooltip title="Edit Asset">
                        <Button
                            icon={<EditOutlined />}
                            size="small"
                            onClick={() => {
                                setSelectedAsset(r);
                                setEditModalVisible(true);
                            }}
                        />
                    </Tooltip>

                    {/* Reverse Transaction Button - only show if not already reversed */}
                    {!r.reversed && (
                        <Tooltip title="Reverse Transaction">
                            <Button icon={<RollbackOutlined />} size="small" danger onClick={() => openReversalModal(r)} />
                        </Tooltip>
                    )}
                    <Dropdown menu={rowMenu(r)} trigger={['click']}>
                        <Button size="small" type="text" icon={<MoreOutlined />} />
                    </Dropdown>
                </Space>
            )
        }
    ];

    const addLineMenu = {
        items: [
            {
                key: '__h',
                label: (
                    <Text type="secondary" style={{ fontSize: 11 }}>
                        Quick add:
                    </Text>
                ),
                disabled: true
            },
            ...assetTypes.map((t) => ({
                key: t.slug,
                label: (
                    <Space>
                        <Text>{t.name}</Text>
                    </Space>
                )
            })),
            { type: 'divider' },
            { key: '__blank', label: 'Blank row' }
        ],
        onClick: ({ key }) => addRow(key === '__blank' ? null : key)
    };

    const hasLivestock = lineItems.some((r) => assetTypes.find((t) => t.slug === r.type_slug)?.is_livestock !== false);
    const hasPhysical = lineItems.some((r) => assetTypes.find((t) => t.slug === r.type_slug)?.is_livestock === false);

    // ── build export URL from current filters ─────────────────────────────────
    const buildExportUrl = (base, filters, format) => {
        const qs = new URLSearchParams({ format, ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== undefined)) });
        return `${base}?${qs}`;
    };

    const ReversalModal = ({ visible, record, onClose, onSuccess, currentUser, apiUrl }) => {
        const [form] = Form.useForm();
        const [submitting, setSubmitting] = useState(false);
        const [reversalType, setReversalType] = useState('');
        const [availableEvents, setAvailableEvents] = useState([]);
        const [loadingEvents, setLoadingEvents] = useState(false);
        const [selectedEvent, setSelectedEvent] = useState(null);

        // console.log('logi', record);

        // Fetch reversible events for this asset from animal_events table
        useEffect(() => {
            if (visible && record?.id) {
                fetchReversibleEvents();
            }
        }, [visible, record]);

        const fetchReversibleEvents = async () => {
            setLoadingEvents(true);
            try {
                const response = await api.get(`/animal-assets/${record.id}/reversible-events`);
                if (response.data.success) {
                    setAvailableEvents(response.data.events);
                }
            } catch (error) {
                console.error('Error fetching reversible events:', error);
                message.error('Failed to load reversible transactions');
            } finally {
                setLoadingEvents(false);
            }
        };

        const getReversalOptions = () => {
            if (!availableEvents.length) return [];
            const options = [];

            // Map event types to reversal options
            for (const event of availableEvents) {
                let label = '';
                let description = '';

                switch (event.eventType) {
                    case 'purchase':
                        label = 'Reverse Purchase';
                        description = `This will remove this asset from inventory. Original purchase on ${dayjs(event.eventDate).format(
                            'YYYY-MM-DD'
                        )}`;
                        break;
                    case 'weaning':
                        label = 'Reverse Weaning';
                        description = `This will remove the adult animal and restore the weaner to active status.`;
                        break;
                    case 'weaned':
                        label = 'Reverse Weaning';
                        description = `This will remove the adult animal and restore the weaner to active status.`;
                        break;
                    case 'birth':
                    case 'siring':
                        label = 'Reverse Birth/Siring';
                        description = `This will remove the offspring and restore the parent's status.`;
                        break;
                    case 'tag_change':
                        const details = event.details ? JSON.parse(event.details) : {};
                        label = 'Reverse Tag Change';
                        description = `This will revert the tag from ${details.new_tag || 'new'} back to ${details.old_tag || 'original'}.`;
                        break;
                    case 'sale':
                        label = 'Reverse Sale';
                        description = `This will restore this asset to inventory. Sold on ${dayjs(event.eventDate).format('YYYY-MM-DD')}`;
                        break;
                    case 'movement':
                        label = 'Reverse Movement';
                        description = `This will move the asset back to its previous location.`;
                        break;
                    default:
                        label = `Reverse ${event.eventType}`;
                        description = `Reverse the ${event.eventType} transaction.`;
                }
                options.push({
                    value: event.id,
                    label: label,
                    description: description,
                    eventType: event.eventType,
                    eventDate: event.eventDate,
                    details: event.details
                });
            }

            return options;
        };

        const handleEventSelect = (eventId) => {
            const event = availableEvents.find((e) => e.id === eventId);
            setSelectedEvent(event);
            setReversalType(event?.eventType);
        };

        const handleSubmit = async (values) => {
            if (!selectedEvent) {
                message.error('Please select a transaction to reverse');
                return;
            }

            setSubmitting(true);
            try {
                const payload = {
                    eventId: selectedEvent.id,
                    eventType: selectedEvent.eventType,
                    reason: values.reason,
                    reason_detail: values.reason_detail,
                    userId: currentUser?.id,
                    userName: currentUser?.name
                };

                const response = await api.post(`/animal-assets/reverse-event`, payload);

                if (response.data.success) {
                    message.success(`${selectedEvent.eventType} reversed successfully`);
                    form.resetFields();
                    onSuccess?.();
                    onClose();
                }
            } catch (error) {
                console.error('Error reversing transaction:', error);
                message.error(error.response?.data?.message || 'Failed to reverse transaction');
            } finally {
                setSubmitting(false);
            }
        };

        const reversalOptions = getReversalOptions();

        // Helper to get event icon and color
        const getEventInfo = (eventType) => {
            const eventMap = {
                purchase: { icon: '📦', color: '#1677ff' },
                weaning: { icon: '🐖➡️🐷', color: '#fa8c16' },
                birth: { icon: '👶', color: '#eb2f96' },
                siring: { icon: '👶', color: '#eb2f96' },
                tag_change: { icon: '🏷️', color: '#722ed1' },
                sale: { icon: '💰', color: '#52c41a' },
                movement: { icon: '🚚', color: '#13c2c2' }
            };
            return eventMap[eventType] || { icon: '📝', color: '#666' };
        };

        return (
            <Modal
                title={
                    <Space>
                        <RollbackOutlined style={{ color: '#ff4d4f' }} />
                        <span>Reverse Transaction</span>
                    </Space>
                }
                open={visible}
                onCancel={() => {
                    onClose();
                    form.resetFields();
                    setSelectedEvent(null);
                    setReversalType('');
                }}
                width={600}
                footer={[
                    <Button key="cancel" onClick={onClose}>
                        Cancel
                    </Button>,
                    <Button
                        key="reverse"
                        type="primary"
                        danger
                        loading={submitting}
                        onClick={() => form.submit()}
                        disabled={!selectedEvent}
                    >
                        Confirm Reversal
                    </Button>
                ]}
            >
                <Alert
                    message="Warning: This action cannot be undone"
                    description="Reversing a transaction will restore inventory and create reversal records for audit purposes."
                    type="warning"
                    showIcon
                    icon={<WarningOutlined />}
                    style={{ marginBottom: 16 }}
                />

                {/* Asset Information */}
                {record && (
                    <Descriptions size="small" bordered column={2} style={{ marginBottom: 16 }}>
                        <Descriptions.Item label="Asset ID">{record.id}</Descriptions.Item>
                        <Descriptions.Item label="Tag Number">
                            <Tag color="blue">{record.tagNumber || record.batchId}</Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Type">
                            <Tag color={record.typeColor}>{record.typeName || record.type_slug}</Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Status">
                            <Tag color={record.status === 'alive' ? 'green' : 'orange'}>{record.status}</Tag>
                        </Descriptions.Item>
                        {record.parentTagNumber && (
                            <Descriptions.Item label="Parent Tag" span={2}>
                                <Tag color="purple">{record.parentTagNumber}</Tag>
                            </Descriptions.Item>
                        )}
                        <Descriptions.Item label="Current Value" span={2}>
                            KES {record.currentValue?.toLocaleString() || 0}
                        </Descriptions.Item>
                    </Descriptions>
                )}

                <Spin spinning={loadingEvents}>
                    <Form form={form} onFinish={handleSubmit} layout="vertical">
                        <Form.Item
                            name="event_id"
                            label="Select transaction to reverse"
                            rules={[{ required: true, message: 'Please select a transaction' }]}
                        >
                            <Select
                                placeholder="Select a transaction to reverse"
                                onChange={handleEventSelect}
                                showSearch
                                optionFilterProp="children"
                            >
                                {reversalOptions.map((opt) => {
                                    const eventInfo = getEventInfo(opt.eventType);
                                    return (
                                        <Option key={opt.value} value={opt.value}>
                                            <Space direction="vertical" size={0} style={{ width: '100%' }}>
                                                <div>
                                                    <span style={{ marginRight: 8 }}>{eventInfo.icon}</span>
                                                    <strong>{opt.label}</strong>
                                                    <Tag color={eventInfo.color} style={{ marginLeft: 8 }}>
                                                        {opt.eventType}
                                                    </Tag>
                                                </div>
                                                <div style={{ fontSize: 12, color: '#666' }}>{opt.description}</div>
                                                <div style={{ fontSize: 11, color: '#999' }}>
                                                    Date: {dayjs(opt.eventDate).format('YYYY-MM-DD')}
                                                </div>
                                            </Space>
                                        </Option>
                                    );
                                })}
                            </Select>
                        </Form.Item>

                        {selectedEvent && (
                            <div style={{ marginBottom: 16, padding: 12, background: '#f5f5f5', borderRadius: 8 }}>
                                <div style={{ marginBottom: 8 }}>
                                    <strong>Transaction Details:</strong>
                                </div>
                                <div style={{ fontSize: 13 }}>
                                    {selectedEvent.details && typeof selectedEvent.details === 'object' && (
                                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                                            {Object.entries(selectedEvent.details).map(([key, value]) => (
                                                <li key={key}>
                                                    <strong>{key}:</strong> {String(value)}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </div>
                        )}

                        <Form.Item name="reason" label="Reversal Reason" rules={[{ required: true, message: 'Please select a reason' }]}>
                            <Select placeholder="Select a reason">
                                <Option value="wrong_entry">Wrong entry / Data entry error</Option>
                                <Option value="customer_return">Customer return / Cancellation</Option>
                                <Option value="quality_issue">Quality issue / Damaged</Option>
                                <Option value="duplicate">Duplicate entry</Option>
                                <Option value="other">Other</Option>
                            </Select>
                        </Form.Item>

                        <Form.Item name="reason_detail" label="Additional Details">
                            <TextArea rows={3} placeholder="Provide more details about why this reversal is needed..." />
                        </Form.Item>
                    </Form>
                </Spin>
            </Modal>
        );
    };

    // Edit Asset Modal Component
    const EditAssetModal = ({ visible, asset, onClose, onSuccess, currentUser }) => {
        const [form] = Form.useForm();
        const [submitting, setSubmitting] = useState(false);
        const [silos, setSilos] = useState([]);
        const [farms, setFarms] = useState([]);

        useEffect(() => {
            if (asset && visible) {
                form.setFieldsValue({
                    animalName: asset.animalName,
                    breed: asset.breed,
                    gender: asset.gender,
                    weight: asset.weight,
                    quantity: asset.quantity,
                    health: asset.health,
                    status: asset.status,
                    farm: asset.farm,
                    siloId: asset.siloId,
                    // paymentStatus: asset.paymentStatus,
                    currentValue: asset.currentValue,
                    remarks: asset.remarks,
                    birth: asset.birth ? dayjs(asset.birth) : null
                });
                loadSilos();
                loadFarms();
            }
        }, [asset, visible, form]);

        const loadSilos = async () => {
            try {
                const res = await api.get(`/api/animal-assets/config/silos?type_slug=${asset.type_slug}`);
                setSilos(res.data.data || []);
            } catch (err) {
                console.error('Error loading silos:', err);
            }
        };

        const loadFarms = async () => {
            try {
                const res = await api.get(`/farm`);
                setFarms(res.data || []);
            } catch (err) {
                console.error('Error loading farms:', err);
            }
        };

        const handleSubmit = async (values) => {
            setSubmitting(true);
            try {
                await api.put(`/asset/${asset.id}`, {
                    ...values,
                    current_weight: values.weight,
                    userId: currentUser?.id,
                    userName: currentUser?.name,
                    changeReason: values.changeReason,
                    birth: values.birth ? values.birth.format('YYYY-MM-DD') : null
                });
                message.success('Asset updated successfully');
                onSuccess();
                onClose();
                form.resetFields();
            } catch (error) {
                console.error('Error updating asset:', error);
                message.error(error.response?.data?.message || 'Failed to update asset');
            } finally {
                setSubmitting(false);
            }
        };

        if (!asset) return null;

        return (
            <Modal
                title={
                    <Space>
                        <EditOutlined />
                        Edit Asset: <Text code>{asset.tagNumber || asset.batchId || asset.assetName}</Text>
                    </Space>
                }
                open={visible}
                onCancel={onClose}
                width={600}
                footer={[
                    <Button key="cancel" onClick={onClose}>
                        Cancel
                    </Button>,
                    <Button key="submit" type="primary" loading={submitting} onClick={() => form.submit()}>
                        Save Changes
                    </Button>
                ]}
            >
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="animalName" label="Name">
                                <Input placeholder="Asset name" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="breed" label="Breed / Model">
                                <Input placeholder="Breed or model" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item name="gender" label="Gender">
                                <Select allowClear placeholder="Select gender">
                                    <Option value="male">Male</Option>
                                    <Option value="female">Female</Option>
                                    <Option value="unknown">Unknown</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="weight" label="Weight (kg)">
                                <InputNumber min={0} step={0.1} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        {/* <Col span={8}>
                            <Form.Item name="initial_weight" label="Initial Weight (kg)">
                                <InputNumber min={0} step={0.1} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col> */}
                        <Col span={8}>
                            <Form.Item name="quantity" label="Quantity">
                                <InputNumber min={0} step={1} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="health" label="Health Status">
                                <Select allowClear>
                                    <Option value="healthy">Healthy</Option>
                                    <Option value="sick">Sick</Option>
                                    <Option value="injured">Injured</Option>
                                    <Option value="quarantined">Quarantined</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="birth" label={asset.category === 'livestock' ? 'DOB' : 'Date of Purchase'}>
                                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" placeholder="Select date of birth" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item name="status" label="Status">
                                <Select allowClear>
                                    <Option value="alive">Alive / Active</Option>
                                    <Option value="sold">Sold</Option>
                                    <Option value="mortality">Mortality</Option>
                                    <Option value="weaned">Weaned</Option>
                                    <Option value="retired">Retired</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="farm" label="Farm">
                                <Select allowClear showSearch>
                                    {farms.map((f) => (
                                        <Option key={f.id} value={String(f.id)}>
                                            {f.name}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="siloId" label="Silo">
                                <Select allowClear showSearch>
                                    {silos.map((s) => (
                                        <Option key={s.id} value={s.id}>
                                            {s.silo_number}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        {/* <Col span={12}>
                            <Form.Item name="paymentStatus" label="Payment Status">
                                <Select allowClear>
                                    <Option value="paid">Paid</Option>
                                    <Option value="unpaid">Unpaid</Option>
                                    <Option value="partially_paid">Partially Paid</Option>
                                </Select>
                            </Form.Item>
                        </Col> */}
                        <Col span={12}>
                            <Form.Item name="currentValue" label="Current Value">
                                <InputNumber min={0} step={100} style={{ width: '100%' }} prefix="KES" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="remarks" label="Remarks">
                        <TextArea rows={2} />
                    </Form.Item>

                    <Form.Item name="changeReason" label="Reason for Change" rules={[{ required: true }]}>
                        <TextArea rows={2} placeholder="Why is this change being made?" />
                    </Form.Item>
                </Form>
            </Modal>
        );
    };

    // Dynamic Reversal Modal
    const DynamicReversalModal = ({ visible, asset, onClose, onSuccess, currentUser }) => {
        const [form] = Form.useForm();
        const [submitting, setSubmitting] = useState(false);
        const [transactions, setTransactions] = useState([]);
        const [loading, setLoading] = useState(false);
        useEffect(() => {
            if (visible && asset) {
                loadReversableTransactions();
            }
        }, [visible, asset]);

        const loadReversableTransactions = async () => {
            setLoading(true);
            try {
                const res = await api.get(`/asset/${asset.id}/reversable`);
                setTransactions(res.data.data || []);
                if (res.data.data.length === 0) {
                    message.info('No reversible transactions found for this asset');
                }
            } catch (error) {
                console.error('Error loading transactions:', error);
                message.error('Failed to load reversible transactions');
            } finally {
                setLoading(false);
            }
        };

        const handleSubmit = async (values) => {
            setSubmitting(true);
            try {
                await api.post(`/reverse-asset/${asset.id}`, {
                    transactionType: values.transactionType,
                    reason: values.reason,
                    reason_detail: values.reason_detail,
                    userId: currentUser?.id,
                    userName: currentUser?.name
                });
                message.success('Transaction reversed successfully');
                onSuccess();
                onClose();
                form.resetFields();
            } catch (error) {
                console.error('Error reversing transaction:', error);
                message.error(error.response?.data?.message || 'Failed to reverse transaction');
            } finally {
                setSubmitting(false);
            }
        };

        if (!asset) return null;

        const transactionOptions = transactions.map((t) => ({
            value: t.type,
            label: t.label,
            description: t.description,
            disabled: !t.canReverse
        }));

        return (
            <Modal
                title={
                    <Space>
                        <RollbackOutlined style={{ color: '#ff4d4f' }} />
                        <span>Reverse Transaction</span>
                    </Space>
                }
                open={visible}
                onCancel={onClose}
                width={550}
                footer={[
                    <Button key="cancel" onClick={onClose}>
                        Cancel
                    </Button>,
                    <Button key="reverse" type="primary" danger loading={submitting} onClick={() => form.submit()}>
                        Confirm Reversal
                    </Button>
                ]}
            >
                <Alert
                    message="Warning: This action cannot be undone"
                    description="Reversing will create an audit trail and restore previous state where applicable."
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                />

                <Descriptions size="small" bordered column={1} style={{ marginBottom: 16 }}>
                    <Descriptions.Item label="Asset">{asset.tagNumber || asset.batchId || asset.assetName}</Descriptions.Item>
                    <Descriptions.Item label="Type">{asset.typeName || asset.type_slug}</Descriptions.Item>
                    <Descriptions.Item label="Current Status">
                        <Tag color={asset.status === 'alive' ? 'green' : 'orange'}>{asset.status}</Tag>
                    </Descriptions.Item>
                </Descriptions>

                <Spin spinning={loading}>
                    <Form form={form} onFinish={handleSubmit} layout="vertical">
                        <Form.Item
                            name="transactionType"
                            label="What would you like to reverse?"
                            rules={[{ required: true, message: 'Please select a transaction to reverse' }]}
                        >
                            <Select placeholder="Select transaction type" loading={loading}>
                                {transactionOptions.map((opt) => (
                                    <Option key={opt.value} value={opt.value} disabled={opt.disabled}>
                                        <div>
                                            <strong>{opt.label}</strong>
                                            <div style={{ fontSize: 12, color: opt.disabled ? '#ccc' : '#666' }}>{opt.description}</div>
                                        </div>
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>

                        <Form.Item name="reason" label="Reversal Reason" rules={[{ required: true, message: 'Please select a reason' }]}>
                            <Select placeholder="Select a reason">
                                <Option value="wrong_entry">Wrong entry / Data entry error</Option>
                                <Option value="customer_return">Customer return / Cancellation</Option>
                                <Option value="quality_issue">Quality issue / Damaged</Option>
                                <Option value="duplicate">Duplicate entry</Option>
                                <Option value="other">Other</Option>
                            </Select>
                        </Form.Item>

                        <Form.Item name="reason_detail" label="Additional Details">
                            <TextArea rows={3} placeholder="Provide more details..." />
                        </Form.Item>
                    </Form>
                </Spin>
            </Modal>
        );
    };

    return (
        <>
            <Card
                title="Assets Management"
                style={{ marginBottom: 20 }}
                extra={
                    <Space>
                        <Tooltip title="Tag untagged offspring">
                            <Badge count={queueCount} offset={[-4, 4]}>
                                <Button icon={<TagOutlined />} onClick={() => setQueueOpen(true)}>
                                    Tag Offspring
                                </Button>
                            </Badge>
                        </Tooltip>
                        <Link to="/animal-assets/configure">
                            <Button icon={<SettingOutlined />}>
                                {/* <Button icon={<SettingOutlined />} onClick={() => setConfigOpen(true)}> */}
                                Configure
                            </Button>
                        </Link>
                        <Link to="/animal-assets/silo-config">
                            <Button icon={<SignalFilled />}>
                                {/* <Button icon={<SettingOutlined />} onClick={() => setConfigOpen(true)}> */}
                                Silo
                            </Button>
                        </Link>
                        <Link to="/animal-assets/mortality">
                            <Button icon={<ExceptionOutlined />}>
                                {/* <Button icon={<SettingOutlined />} onClick={() => setConfigOpen(true)}> */}
                                Mortality
                            </Button>
                        </Link>
                        <Button icon={<ReloadOutlined />} onClick={loadConfig} />
                        <Link to="/animal-assets/add-invoice">
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                // onClick={() => handleInvoiceOpen()}
                                // onClick={() => setInvoiceOpen(true)}
                            >
                                New Invoice
                            </Button>
                        </Link>
                    </Space>
                }
            >
                <Tabs
                    activeKey={mainTab}
                    onChange={setMainTab}
                    // type="card"
                    items={[
                        /* ═══ DASHBOARD ═══ */
                        {
                            key: 'dashboard',
                            label: (
                                <Space>
                                    <BarChartOutlined />
                                    Dashboard
                                </Space>
                            ),
                            children: <StatsDashboard farms={farms} />
                        },
                        /* ═══ INVOICE HEADERS TAB ═══ */
                        {
                            key: 'headers',
                            label: (
                                <Space>
                                    <FileTextOutlined />
                                    Invoice Headers
                                </Space>
                            ),
                            children: (
                                <>
                                    <ProTable
                                        actionRef={invoiceRef}
                                        columns={invoiceCols}
                                        rowKey="id"
                                        search={false}
                                        request={async (params, sort) => {
                                            const qs = new URLSearchParams({
                                                page: params.current || 1,
                                                limit: params.pageSize || 20,
                                                ...invoiceFilters
                                            });
                                            if (sort) {
                                                const f = Object.keys(sort)[0];
                                                if (f) qs.set('sort', `${f}:${sort[f] === 'ascend' ? 'ASC' : 'DESC'}`);
                                            }
                                            const d = await apiFetch(`/api/animal-assets/invoices?${qs}`);
                                            return { data: d.data, total: d.total, success: true };
                                        }}
                                        pagination={{ defaultPageSize: 20, showSizeChanger: true }}
                                        scroll={{ x: 1300 }}
                                        defaultSize="small"
                                        options={{ reload: true, density: true, setting: true }}
                                        params={{ _f: JSON.stringify(invoiceFilters) }}
                                        toolBarRender={() => [
                                            <>
                                                <FilterBar
                                                    filters={invoiceFilters}
                                                    onFilterChange={updateInvFilter}
                                                    onReset={() => {
                                                        setInvoiceFilters({});
                                                        invoiceRef.current?.reload();
                                                    }}
                                                    stakeholders={stakeholders}
                                                    farms={farms}
                                                    showStatusFilter={false}
                                                />
                                                <Tooltip title="Export current filter as CSV">
                                                    <Button
                                                        size="small"
                                                        icon={<ExportOutlined />}
                                                        onClick={() =>
                                                            doExport(
                                                                buildExportUrl('/api/animal-assets/invoices/export', invoiceFilters, 'csv'),
                                                                'invoices.csv'
                                                            )
                                                        }
                                                    >
                                                        CSV
                                                    </Button>
                                                </Tooltip>
                                            </>
                                        ]}
                                    />
                                </>
                            )
                        },

                        /* ═══ ASSETS / ITEMS TAB ═══ */
                        {
                            key: 'items',
                            label: (
                                <Space>
                                    <BarChartOutlined />
                                    Assets / Items
                                </Space>
                            ),
                            children: (
                                <>
                                    {/* Bulk bar */}
                                    {selectedKeys.length > 0 && (
                                        <div
                                            style={{
                                                background: '#e6f4ff',
                                                border: '1px solid #91caff',
                                                borderRadius: 8,
                                                padding: '10px 16px',
                                                marginBottom: 8,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 10,
                                                flexWrap: 'wrap'
                                            }}
                                        >
                                            <Badge count={selectedKeys.length} color="#1677ff" />
                                            <Text strong>Selected</Text>
                                            <Divider type="vertical" />
                                            {ACTIONS.map((a) => (
                                                <Button
                                                    key={a.key}
                                                    size="small"
                                                    icon={a.icon}
                                                    style={{ borderColor: a.color, color: a.color }}
                                                    onClick={() => {
                                                        setBulkAction(a.key);
                                                        setBulkResult(null);
                                                        setBulkModal(true);
                                                    }}
                                                >
                                                    {a.label}
                                                </Button>
                                            ))}
                                            <Button size="small" type="text" danger onClick={() => setSelectedKeys([])}>
                                                Clear
                                            </Button>
                                        </div>
                                    )}

                                    {/* Export toolbar */}

                                    <ProTable
                                        actionRef={assetRef}
                                        columns={assetCols}
                                        rowKey="id"
                                        search={false}
                                        request={async (params, sort) => {
                                            const qs = new URLSearchParams({
                                                page: params.current || 1,
                                                limit: params.pageSize || 20,
                                                ...assetFilters
                                            });
                                            if (sort) {
                                                const f = Object.keys(sort)[0];
                                                if (f) qs.set('sort', `${f}:${sort[f] === 'ascend' ? 'ASC' : 'DESC'}`);
                                            }
                                            const d = await apiFetch(`/api/animal-assets?${qs}`);
                                            return { data: d.data, total: d.total, success: true };
                                        }}
                                        rowSelection={{
                                            selectedRowKeys: selectedKeys,
                                            onChange: (keys) => setSelectedKeys(keys),
                                            preserveSelectedRowKeys: true
                                        }}
                                        pagination={{ defaultPageSize: 20, showSizeChanger: true }}
                                        scroll={{ x: 1500 }}
                                        defaultSize="small"
                                        options={{ reload: true, density: true, setting: true }}
                                        params={{ _f: JSON.stringify(assetFilters) }}
                                        toolBarRender={() => [
                                            <>
                                                <FilterBar
                                                    filters={assetFilters}
                                                    onFilterChange={updateAstFilter}
                                                    onReset={() => {
                                                        setAssetFilters({});
                                                        setAssetSubTab('all');
                                                        assetRef.current?.reload();
                                                    }}
                                                    assetTypes={assetTypes}
                                                    farms={farms}
                                                />
                                                <Tooltip title="Export current filter as CSV">
                                                    <Button
                                                        size="small"
                                                        icon={<ExportOutlined />}
                                                        onClick={() =>
                                                            doExport(
                                                                buildExportUrl('/api/animal-assets/export', assetFilters, 'csv'),
                                                                'assets.csv'
                                                            )
                                                        }
                                                    >
                                                        CSV
                                                    </Button>
                                                </Tooltip>
                                                <Tooltip title="Export current filter as Excel">
                                                    <Button
                                                        size="small"
                                                        icon={<ExportOutlined />}
                                                        onClick={() =>
                                                            doExport(
                                                                buildExportUrl('/api/animal-assets/export', assetFilters, 'excel'),
                                                                'assets.xls'
                                                            )
                                                        }
                                                    >
                                                        Excel
                                                    </Button>
                                                </Tooltip>
                                            </>
                                        ]}
                                    />
                                </>
                            )
                        }
                    ]}
                />

                {/* ══ NEW INVOICE MODAL ══ */}
                <Modal
                    title={
                        <Space>
                            <FileTextOutlined />
                            New Asset Purchase Invoice
                        </Space>
                    }
                    open={invoiceOpen}
                    onCancel={() => {
                        setInvoiceOpen(false);
                        headerForm.resetFields();
                        setLineItems([]);
                    }}
                    width={1380}
                    footer={null}
                    destroyOnClose
                >
                    <Card size="small" style={{ marginBottom: 12, background: '#fafafa' }}>
                        <Form form={headerForm} layout="vertical">
                            <Row gutter={12}>
                                <Col xs={24} md={5}>
                                    <Form.Item name="stakeholderId" label="Supplier" rules={[{ required: true }]}>
                                        <Select
                                            showSearch
                                            placeholder="Select supplier"
                                            optionFilterProp="children"
                                            loading={loading}
                                            value={selectedStakeholder}
                                            onChange={(value) => {
                                                setSelectedStakeholder(value);

                                                headerForm.setFieldsValue({
                                                    stakeholderId: value
                                                });
                                            }}
                                            dropdownRender={(menu) => (
                                                <>
                                                    {menu}
                                                    <Divider style={{ margin: '4px 0' }} />
                                                    <div
                                                        style={{
                                                            padding: '4px 8px',
                                                            color: '#1677ff',
                                                            cursor: 'pointer',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 8
                                                        }}
                                                        onClick={() => setStakeholderModalVisible(true)}
                                                    >
                                                        <PlusOutlined />
                                                        <span>Add supplier</span>
                                                    </div>
                                                </>
                                            )}
                                        >
                                            {stakeholders.map((s) => (
                                                <Option key={s.id} value={s.id}>
                                                    {s.name}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>

                                    {/* Stakeholder Modal */}
                                    <StakeholderModal
                                        visible={stakeholderModalVisible}
                                        onCancel={() => setStakeholderModalVisible(false)}
                                        onSuccess={(newStakeholder) => {
                                            handleStakeholderAdded(newStakeholder);
                                            setStakeholderModalVisible(false);
                                        }}
                                    />
                                </Col>
                                <Col xs={12} md={3}>
                                    <Form.Item name="farm" label="Primary Farm">
                                        <Select
                                            allowClear
                                            dropdownRender={(menu) => (
                                                <>
                                                    {menu}
                                                    <Divider style={{ margin: '4px 0' }} />
                                                    <div
                                                        style={{ padding: '4px 8px', color: '#1677ff', cursor: 'pointer' }}
                                                        onClick={() => message.info('Open Farm Management to add')}
                                                    >
                                                        <PlusOutlined /> Add farm
                                                    </div>
                                                </>
                                            )}
                                        >
                                            {farms?.map((f) => (
                                                <Option key={f.id} value={String(f.id)}>
                                                    {f.name}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col xs={12} md={3}>
                                    <Form.Item name="recorded" label="Invoice Date" rules={[{ required: true }]} initialValue={dayjs()}>
                                        <DatePicker style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>

                                <Col xs={12} md={3}>
                                    <Form.Item name="dueDate" label="Due Date">
                                        <DatePicker style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>

                                <Col xs={12} md={3}>
                                    <Form.Item name="paymentMethod" label="Method">
                                        <Select allowClear>
                                            {['Cash', 'Bank Transfer', 'Cheque', 'Mobile Money'].map((m) => (
                                                <Option key={m} value={m.toLowerCase().replace(/ /g, '_')}>
                                                    {m}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>

                                <Col xs={24}>
                                    <Form.Item name="remarks" label="Remarks">
                                        <TextArea rows={3} />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Form>
                    </Card>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <Text strong>Line Items</Text>
                        <Dropdown menu={addLineMenu} trigger={['click']}>
                            <Button type="dashed" icon={<PlusOutlined />} size="small">
                                Add Item <DownOutlined />
                            </Button>
                        </Dropdown>
                    </div>

                    {lineItems.length === 0 ? (
                        <div
                            onClick={() => addRow(null)}
                            style={{
                                border: '2px dashed #d9d9d9',
                                borderRadius: 8,
                                padding: '28px 0',
                                textAlign: 'center',
                                cursor: 'pointer',
                                color: '#aaa',
                                marginBottom: 12
                            }}
                        >
                            <PlusOutlined style={{ fontSize: 20, marginBottom: 4, display: 'block' }} />
                            Click or use "Add Item ▾"
                        </div>
                    ) : (
                        <div style={{ overflowX: 'auto', marginBottom: 12 }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, color: '#fff' }}>
                                <thead>
                                    <tr style={{ background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
                                        {[
                                            'Type',
                                            'Subtype',
                                            'Name',
                                            'Qty',
                                            'Unit Cost',
                                            'Total',
                                            ...(hasLivestock ? ['Birth', 'Silo'] : []),
                                            ...(hasPhysical ? ['Asset Name', 'Serial #', 'Life (y)'] : []),
                                            'Farm',
                                            ''
                                        ].map((h) => (
                                            <th
                                                key={h}
                                                style={{
                                                    padding: '6px 4px',
                                                    textAlign: 'left',
                                                    fontWeight: 500,
                                                    color: '#555',
                                                    whiteSpace: 'nowrap',
                                                    fontSize: 11
                                                }}
                                            >
                                                {h}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {lineItems.map((item, idx) => (
                                        <LineRow
                                            key={item._key}
                                            item={item}
                                            idx={idx}
                                            onChange={updateRow}
                                            onRemove={removeRow}
                                            farms={farms}
                                            silos={silos}
                                            assetTypes={assetTypes}
                                            subtypeMap={subtypeMap}
                                            onPreviewTag={previewTag}
                                        />
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {lineItems.length > 0 && (
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: 24,
                                padding: '8px 4px',
                                borderTop: '1px solid #f0f0f0',
                                marginBottom: 12
                            }}
                        >
                            <span>
                                <Text type="secondary">Items: </Text>
                                <Text strong>{lineItems.length}</Text>
                            </span>
                            <span>
                                <Text type="secondary">Total: </Text>
                                <Text strong style={{ fontSize: 16, color: '#1677ff' }}>
                                    {fmt(subtotal)}
                                </Text>
                            </span>
                        </div>
                    )}

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                        <Button
                            onClick={() => {
                                setInvoiceOpen(false);
                                headerForm.resetFields();
                                setLineItems([]);
                            }}
                        >
                            Cancel
                        </Button>
                        <Popconfirm
                            title="Save invoice?"
                            description={`${lineItems.length} item(s) — Total: ${fmt(subtotal)}`}
                            onConfirm={saveInvoice}
                        >
                            <Button type="primary" icon={<SaveOutlined />} loading={saving} disabled={!lineItems.length}>
                                Save Invoice
                            </Button>
                        </Popconfirm>
                    </div>
                </Modal>

                {/* ══ INVOICE DRAWER ══ */}
                <InvoiceDrawer
                    invoiceId={drawerId}
                    defaultTab={drawerTab}
                    open={drawerOpen}
                    onClose={() => setDrawerOpen(false)}
                    currentUser={currentUser}
                    users={users}
                    paidOptions={paidOptions}
                    onRefresh={() => invoiceRef.current?.reload()}
                />

                {/* ══ BULK MODAL ══ */}
                <Modal
                    title={
                        <Space>
                            {ACTIONS.find((a) => a.key === bulkAction)?.icon}
                            {ACTIONS.find((a) => a.key === bulkAction)?.label}
                            <Tag>
                                {selectedKeys.length} record{selectedKeys.length !== 1 ? 's' : ''}
                            </Tag>
                        </Space>
                    }
                    open={bulkModal}
                    onCancel={() => {
                        setBulkModal(false);
                        setBulkResult(null);
                    }}
                    footer={null}
                    destroyOnClose
                    width={540}
                >
                    {bulkResult ? (
                        <>
                            <Alert
                                type={bulkResult.failed === 0 ? 'success' : 'warning'}
                                message={`${bulkResult.ok} ok, ${bulkResult.failed} failed`}
                                style={{ marginBottom: 12 }}
                            />
                            {bulkResult.failed > 0 && (
                                <ul style={{ fontSize: 12, color: '#f5222d' }}>
                                    {bulkResult.results
                                        .filter((r) => !r.success)
                                        .map((r) => (
                                            <li key={r.id}>
                                                ID {r.id}: {r.message}
                                            </li>
                                        ))}
                                </ul>
                            )}
                            <Button
                                block
                                onClick={() => {
                                    setBulkModal(false);
                                    setBulkResult(null);
                                }}
                            >
                                Done
                            </Button>
                        </>
                    ) : (
                        <ActionForm
                            action={bulkAction}
                            farms={farms}
                            silos={silos}
                            onFinish={submitBulk}
                            loading={bulkLoading}
                            count={selectedKeys.length}
                            type_slug={assetTypes}
                        />
                    )}
                </Modal>

                {/* ══ ROW ACTION MODAL ══ */}
                <Modal
                    title={
                        rowAction && (
                            <Space>
                                {ACTIONS.find((a) => a.key === rowAction.action)?.icon}
                                {ACTIONS.find((a) => a.key === rowAction.action)?.label}
                                <Tag color={rowAction.record?.typeColor}>{rowAction.record?.typeName}</Tag>
                                <Text code style={{ fontSize: 12 }}>
                                    {rowAction.record?.tagNumber || rowAction.record?.batchId || rowAction.record?.assetName}
                                </Text>
                            </Space>
                        )
                    }
                    open={rowModal}
                    onCancel={() => setRowModal(false)}
                    footer={null}
                    destroyOnClose
                    width={500}
                >
                    <ActionForm
                        action={rowAction?.action}
                        farms={farms}
                        silos={silos}
                        onFinish={submitRowAction}
                        loading={rowLoading}
                        count={1}
                        type_slug={assetTypes}
                    />
                </Modal>

                {/* ══ STATEMENT ══ */}
                <StatementDrawer assetId={statId} open={statOpen} onClose={() => setStatOpen(false)} />

                {/* ══ OFFSPRING QUEUE ══ */}
                <OffspringQueueDrawer
                    open={queueOpen}
                    onClose={() => setQueueOpen(false)}
                    farms={farms}
                    silos={silos}
                    assetTypes={assetTypes}
                    subtypeMap={subtypeMap}
                    currentUser={currentUser}
                    onTagged={() => {
                        loadConfig();
                        assetRef.current?.reload();
                    }}
                />

                {/* Reversal Modal */}
                <ReversalModal
                    visible={reversalModalVisible}
                    record={selectedForReversal}
                    onClose={() => {
                        setReversalModalVisible(false);
                        setSelectedForReversal(null);
                    }}
                    onSuccess={handleReversalSuccess}
                    currentUser={currentUser}
                />

                {/* Edit Asset Modal */}
                <EditAssetModal
                    visible={editModalVisible}
                    asset={selectedAsset}
                    onClose={() => {
                        setEditModalVisible(false);
                        setSelectedAsset(null);
                    }}
                    onSuccess={() => {
                        assetRef.current?.reload();
                        loadConfig();
                    }}
                    currentUser={currentUser}
                />

                {/* Dynamic Reversal Modal */}
                <DynamicReversalModal
                    visible={reversalModalVisible}
                    asset={selectedAsset}
                    onClose={() => {
                        setReversalModalVisible(false);
                        setSelectedAsset(null);
                    }}
                    onSuccess={() => {
                        assetRef.current?.reload();
                        loadConfig();
                    }}
                    currentUser={currentUser}
                />

                {/* ══ CONFIG ══ */}
                <ConfigModal open={configOpen} onClose={() => setConfigOpen(false)} onRefresh={loadConfig} />
            </Card>
        </>
    );
};

export default AnimalAssetsPage;
